/**
 * ea-datalayer.js
 * Edible × Shopify Replatform — Analytics Event Layer
 *
 * Fires window.dataLayer.push() for every MVP event so GTM can route to
 * Adobe Analytics, Segment, and GA4 from a single source of truth. One
 * canonical schema; all destination-specific reshaping (GA4 items[]/currency,
 * Adobe eVars/s.events, Segment properties) is done in GTM, not here.
 *
 * Architecture:
 *  - window.EAPageData  → set by layout/theme.liquid (Liquid vars → JS)
 *  - getSharedContext() → merged onto every event automatically
 *  - Checkout events    → handled separately in the Shopify Customer Events
 *                         pixel sandbox (cart_add, checkout steps, purchase)
 *
 * Screens covered here (theme code layer):
 *  1.  page_view          — every route load
 *  2.  nav_click          — header + footer links, mega-menu
 *  3.  module_click       — hero, skinny banner, promo tiles
 *  4.  module_view        — impressions (IntersectionObserver)
 *  5.  filter_apply       — PLP/search filters & sort
 *  6.  search             — predictive + keyword submit + null-results
 *  7.  cart_add           — Add to Cart success (theme cart:update event)
 *  7b. product_removed    — item/qty removed (diffed against /cart.js snapshot)
 *  7c. coupon_applied     — discount code applied or failed (cart-side)
 *  8.  product_view       — PDP load
 *  8b. view_item_list     — collection / PLP load (GA4 ecommerce)
 *  8c. category-browse    — search event on collection/PLP load
 *  9.  site_error         — 404, null-search, area-not-serviced (+ generic
 *                           ea:site-error for order-track/form/payment errors)
 *  10. form_view          — newsletter / contact form visible
 *  11. form_start         — first field focus
 *  12. form_complete      — form submit success (+ optInProperty)
 *
 * Direct Segment calls (EA-4411, the only exception to "GTM reshapes all"):
 *  - identify only (page view, login, newsletter signup): raw customer_id as
 *    userId (logged in), or Segment's own anonymousId (guest) — never hashed,
 *    never with traits. No direct track() calls: newsletter signups reach
 *    Segment as GTM's Form Submitted.
 *  - reset() on logout / account switch.
 *  - 'Order Completed' (+ byob) comes from docs/segment-checkout-pixel.js.
 *  PII policy: no name / email / phone / address ever reaches Segment.
 *  13. zip_entered        — zip / serviceability check
 *  13b. referral_store_captured — EA-4288 franchise referral link established an
 *                           attribution (via ea:referral-store-captured)
 *  14. loyalty_enrolled / reward_redeemed — via ea:loyalty-enrolled /
 *                           ea:reward-redeemed (contracts; feature not built yet)
 *
 * GA4 Additional Suggested (CRO) events:
 *  8d. select_item        — product card click in a list (+ view_item_list items)
 *  15. cart_viewed        — cart drawer open / cart page load
 *  16. scroll_depth       — 25/50/75/90% thresholds, once per page
 *  17. delivery_method_selected — via ea:delivery-method-selected (contract)
 *      cart_quantity_changed    — stepper increase on an existing cart line
 *      free_delivery_progress   — cart value below the free-delivery threshold
 *
 * Checkout (begin_checkout/steps) and purchase are emitted from the Shopify
 * Customer Events Web Pixel, NOT this file.
 */

window.dataLayer = window.dataLayer || [];

/* ─────────────────────────────────────────────────────────────────────────────
   SHARED CONTEXT
   Merged onto every event. Reads from window.EAPageData (set by theme.liquid)
   and sessionStorage (campaign params persist across SPA navigations).
───────────────────────────────────────────────────────────────────────────── */

function getDeviceType() {
  const w = window.innerWidth;
  if (w < 750) return 'mobile';
  if (w < 990) return 'tablet';
  return 'desktop';
}

/**
 * Read UTM / tracking params from the current URL on first entry and cache
 * them in sessionStorage so they survive SPA navigations on the same visit.
 */
function getCampaignParams() {
  const SESSION_KEY = 'ea_campaign_params';
  const current = new URLSearchParams(window.location.search);
  const hasUtm = [...current.keys()].some((k) => k.startsWith('utm_') || k === 'trackingCode');

  if (hasUtm) {
    sessionStorage.setItem(SESSION_KEY, window.location.search);
  }

  const source = new URLSearchParams(sessionStorage.getItem(SESSION_KEY) || '');
  const result = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'trackingCode'].forEach((k) => {
    const v = source.get(k);
    if (v) result[k] = v;
  });
  return result;
}

function getPathDepth() {
  const root = (window.Shopify?.routes?.root || '/').replace(/\/+$/, '');
  let path = window.location.pathname;
  if (root && (path === root || path.startsWith(`${root}/`))) path = path.slice(root.length);
  return path.split('/').filter(Boolean).length;
}

/**
 * Guest new-vs-returning, decided once per browser session and then frozen.
 * - sessionStorage holds this session's verdict, so every push in the session
 *   (and every page of it) reports the same value.
 * - localStorage `ea_has_visited` marks that the browser has had a previous
 *   session; it is only read when the session verdict is first made.
 * @returns {'new'|'returning'|null}
 */
function getGuestVisitorType() {
  // Memoised on the function itself (not a module-level let) so the function is
  // self-contained — tests extract it by name.
  const memo = /** @type {{value?: 'new'|'returning'|null}} */ (getGuestVisitorType);
  if ('value' in memo) return /** @type {'new'|'returning'|null} */ (memo.value);
  const SESSION_KEY = 'ea_nvr';
  const VISITED_KEY = 'ea_has_visited';
  /** @type {'new'|'returning'|null} */
  let value = null;
  try {
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached === 'new' || cached === 'returning') {
      value = cached;
    } else {
      value = localStorage.getItem(VISITED_KEY) ? 'returning' : 'new';
      sessionStorage.setItem(SESSION_KEY, value);
      localStorage.setItem(VISITED_KEY, '1');
    }
  } catch (_) {
    value = null;
  }
  memo.value = value;
  return value;
}

function getSharedContext() {
  const d = window.EAPageData || {};

  // newVsReturning: Liquid computes this from customer.orders_count for logged-in
  // users. For guests it's null — fall back to getGuestVisitorType(), which is
  // resolved ONCE per session (UAT Defects row 69: the old inline check wrote the
  // localStorage flag on every push, so a first-time visitor flipped from 'new'
  // to 'returning' on the second push of the same page).
  const newVsReturning = d.newVsReturning != null ? d.newVsReturning : getGuestVisitorType();

  return {
    pageUrl: window.location.href,
    pagePath: window.location.pathname,
    // pageDepth: number of non-empty path segments (0 = homepage, 1 = /collections, etc.).
    // The market/locale prefix (/fr-ca, /en-ca) is not depth — without stripping
    // it every localized homepage reported pageDepth 1.
    pageDepth: getPathDepth(),
    pageType: d.pageType || '',
    // pageName from EAPageData (Liquid page_title, clean string) avoids any
    // double-encoding that can affect document.title on some template configs.
    // theme.liquid already decodes page_title's entities; document.title is decoded
    // by the browser. Decoding again here would turn a literal "&amp;" into "&".
    pageName: d.pageName || document.title,
    siteSection: d.siteSection || '',
    serverRegion: d.serverRegion || 'US',
    deviceType: getDeviceType(),
    salesChannel: d.salesChannel || 'web',
    platform: 'web',
    loyaltyStatus: d.loyaltyStatus || 'guest',
    loyaltyTier: d.loyaltyTier || null,
    // ISO currency code (USD/CAD). GA4 `purchase`/ecommerce and Adobe
    // event[order_total] both need this; derived server-side from the store,
    // falls back to serverRegion so it's always present.
    currency: d.currency || (d.serverRegion === 'CA' ? 'CAD' : 'USD'),
    referrer: document.referrer || '',
    authenticationStatus: d.authenticationStatus || 'guest',
    newVsReturning,
    ...(d.customerId ? { userId: String(d.customerId) } : {}),
    // Browse taxonomy / gifting occasion — optional page_view params, only
    // present on collection / occasion-landing pages (set in theme.liquid).
    ...(d.category ? { category: d.category } : {}),
    ...(d.occasion ? { occasion: d.occasion } : {}),
    // EA-4288: the franchise store credited on the current cart (`?store=123` on any
    // URL). Set by assets/referral-attribution.js, which runs
    // first. Attribution only — never the fulfilling store. Snake case like the
    // campaign params below, because it mirrors a landing-URL parameter.
    ...(d.referralStore ? { referral_store: String(d.referralStore) } : {}),
    ...getCampaignParams(),
  };
}

/* ─────────────────────────────────────────────────────────────────────────────
   PUSH HELPER
───────────────────────────────────────────────────────────────────────────── */

/**
 * Push a GTM event with shared context merged in.
 * @param {string} eventName
 * @param {Object} payload
 */
function pushEvent(eventName, payload) {
  const data = { event: eventName, ...getSharedContext(), ...payload };
  window.dataLayer.push(data);
  // Debug logging — silent in production; add ?ea_debug=1 to the URL to enable.
  if (window.location.search.includes('ea_debug=1')) {
    console.log(`%c[EA DataLayer] ${eventName}`, 'color:#ff6b8a;font-weight:bold', data);
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   SEGMENT IDENTITY  (EA-4411)
   The only direct Segment Analytics.js calls in the theme: identify() and
   reset(). Segment itself is injected by GTM behind OneTrust consent, so every
   call waits for window.analytics (whenSegmentReady) and is a silent no-op when
   it never arrives (no consent, GTM off, etc.). Nothing identity-related is
   read or stored before that point, i.e. before analytics consent.

   Identity rules:
    - Logged-in customer → userId = raw Shopify customer_id (unhashed).
    - Guest              → Segment's OWN anonymousId (ajs_anonymous_id). The
                           theme never mints, stores or overrides a guest id, so
                           the first page call, every identify and the checkout
                           pixel's Order Completed (docs/segment-checkout-pixel.js
                           reads ajs_anonymous_id) all carry the same id.
   PII policy: identify() never sends traits. Hashing (if any) is a Segment-side
   concern, never done here.
   Gated, like the rest of this file, by settings.send_datalayer_events.
───────────────────────────────────────────────────────────────────────────── */

const SEGMENT_LAST_CID_KEY = 'ea_seg_last_cid';

/**
 * Legacy guest id the theme used to mint itself (cookie + localStorage mirror).
 * It ran ahead of Segment on a first visit and split one visitor across two
 * anonymous ids, so it is no longer written — only deleted, so a stale copy can't
 * outlive a Segment reset() and be picked up by anything reading it.
 */
const LEGACY_GUEST_UUID_KEY = 'ea_uuid';

function clearLegacyGuestUuid() {
  try {
    if (document.cookie.split('; ').some((row) => row.startsWith(`${LEGACY_GUEST_UUID_KEY}=`))) {
      document.cookie = `${LEGACY_GUEST_UUID_KEY}=; path=/; max-age=0; SameSite=Lax`;
    }
  } catch (_) {}
  try {
    localStorage.removeItem(LEGACY_GUEST_UUID_KEY);
  } catch (_) {}
}

/**
 * Run cb once GTM-injected Segment is usable. Uses analytics.ready() when the
 * real library is present, else polls every 250ms for up to 10s, then gives up.
 * Callbacks run in the order they were registered.
 * @param {(analytics: any) => void} cb
 */
function whenSegmentReady(cb) {
  const run = () => {
    try {
      cb(window.analytics);
    } catch (_) {}
  };
  const isUsable = () =>
    typeof window.analytics !== 'undefined' &&
    typeof window.analytics.identify === 'function' &&
    typeof window.analytics.track === 'function';
  try {
    if (isUsable()) {
      if (typeof window.analytics.ready === 'function') window.analytics.ready(run);
      else run();
      return;
    }
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += 250;
      if (isUsable()) {
        clearInterval(timer);
        try {
          if (typeof window.analytics.ready === 'function') window.analytics.ready(run);
          else run();
        } catch (_) {}
      } else if (elapsed >= 10000) {
        clearInterval(timer);
      }
    }, 250);
  } catch (_) {}
}

/**
 * identify() with no traits, inside analytics.ready: raw customer_id as userId
 * for logged-in customers; for guests a traits-only identify({}), which
 * analytics.js attaches to its own anonymousId. No setAnonymousId — Segment's id
 * is the one used from the first page onward.
 */
function segmentIdentify() {
  const d = window.EAPageData || {};
  const userId = d.customerId ? String(d.customerId) : '';
  whenSegmentReady((analytics) => {
    try {
      if (userId) analytics.identify(userId, {});
      else analytics.identify({});
    } catch (_) {}
  });
}

/**
 * True only when localStorage supports a full set/remove cycle. Private mode or
 * disabled storage throws, in which case the caller must not rely on persisted
 * dedupe state.
 * @returns {boolean}
 */
function storageAvailable() {
  try {
    const probe = '__ea_seg_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Sign-in / logout detection. Hosted Customer Accounts expose no theme hook, so
 * compare the current customer_id with the last one seen in this browser.
 *  - New customer_id → dataLayer `customer_authenticated`. Segment gets ONLY the
 *    identify from initPageView (no 'Signed In' track — not in the tracking plan).
 *  - Different customer than last seen (account switch, no logout seen) →
 *    analytics.reset() first, so the previous user's anonymous session isn't
 *    merged into the new one. A FIRST login (nothing stored) is left alone so
 *    pre-login guest history still stitches to the customer.
 *  - Stored id but no customer → logout → analytics.reset(). Segment mints its
 *    own fresh anonymousId; the theme does not supply one.
 *
 * Runs entirely inside whenSegmentReady, i.e. only after analytics consent, so
 * the last-seen customer id is never stored before consent. Registered before
 * initPageView's identify, so a reset always precedes it.
 *
 * Deduping requires working localStorage; without it auth tracking is skipped.
 */
function initAuthTracking() {
  const d = window.EAPageData || {};
  const currentId = d.customerId ? String(d.customerId) : '';

  whenSegmentReady((analytics) => {
    if (!storageAvailable()) return;

    let storedId = '';
    try {
      storedId = localStorage.getItem(SEGMENT_LAST_CID_KEY) || '';
    } catch (_) {}

    if (currentId && currentId !== storedId) {
      if (storedId) {
        try {
          if (typeof analytics.reset === 'function') analytics.reset();
        } catch (_) {}
      }
      try {
        localStorage.setItem(SEGMENT_LAST_CID_KEY, currentId);
      } catch (_) {}
      const newVsReturning = d.newVsReturning != null ? d.newVsReturning : getGuestVisitorType();
      pushEvent('customer_authenticated', { authenticationStatus: 'logged_in', newVsReturning });
      return;
    }

    if (!currentId && storedId) {
      try {
        localStorage.removeItem(SEGMENT_LAST_CID_KEY);
      } catch (_) {}
      try {
        if (typeof analytics.reset === 'function') analytics.reset();
      } catch (_) {}
    }
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   1. PAGE VIEW
   Fires on every page/SPA route load.
───────────────────────────────────────────────────────────────────────────── */

function initPageView() {
  // EA-4411: identifies customers (raw customer_id) and guests (Segment's own
  // anonymousId). Also drops the legacy theme-minted `ea_uuid`.
  clearLegacyGuestUuid();
  segmentIdentify();
  pushEvent('page_view', {});
}

/* ─────────────────────────────────────────────────────────────────────────────
   2. NAV CLICK
   Delegates on document — captures header (desktop mega-menu + mobile drawer)
   and footer nav links.
───────────────────────────────────────────────────────────────────────────── */

/**
 * Resolve the nav "section" (group heading) for a header nav link.
 *
 * The EA header renders nav items as <details class="tw-desktop-menu-item">
 * with a <summary><a>Top Title</a></summary>, and submenu groups shaped like
 * <li><span>SECTION</span><ul><li><a>child</a></li></ul></li>. The mobile
 * drawer uses the same summary/group structure. The previous selectors
 * (.mega-menu__category / [data-nav-section]) matched none of this, so
 * navSection always came back undefined.
 *
 * @param {HTMLElement} link
 * @returns {string|undefined}
 */
function getHeaderNavSection(link) {
  // Child link inside a submenu group: the group heading is the <span> that
  // precedes the child <ul> (or the group <li>'s own leading <span>).
  const childUl = link.closest('ul');
  if (childUl) {
    const prevSpan = childUl.previousElementSibling;
    if (prevSpan?.tagName === 'SPAN' && prevSpan.textContent?.trim()) {
      return prevSpan.textContent.trim();
    }
    const groupLi = childUl.closest('li');
    const groupSpan = groupLi?.querySelector(':scope > span');
    if (groupSpan?.textContent?.trim()) return groupSpan.textContent.trim();
  }

  // Top-level item (with or without submenu, e.g. "Valentine's Day"): use the
  // trigger's own title from the summary/button.
  const trigger = link.closest('details, [data-trigger-id], .js-nav-trigger, .js-mobile-menu-item');
  const triggerLabel = trigger?.querySelector('summary a, summary button, :scope > a, :scope > button, button, a');
  if (triggerLabel && triggerLabel !== link && triggerLabel.textContent?.trim()) {
    return triggerLabel.textContent.trim();
  }

  // Fallback: a standalone top-level link is its own section.
  return link.textContent?.trim() || undefined;
}

function initNavClicks() {
  document.addEventListener('click', (e) => {
    const link = /** @type {HTMLAnchorElement|null} */ (e.target.closest('a[href]'));
    if (!link) return;

    const href = link.href || '';
    // Ignore anchors pointing to # only or javascript:
    if (!href || href.startsWith('javascript')) return;

    // ── Header nav (desktop menu + mobile drawer) ──────────────────────────
    const headerComponent = link.closest('header-component');
    if (headerComponent) {
      // Skip cart / search icon clicks — those aren't nav links
      if (link.closest('.header__icon')) return;

      // A link is level-2 when it sits inside a submenu list rather than being
      // the top-level trigger link itself.
      const isL2 = Boolean(
        link.closest('.menu-drawer__submenu, .js-mobile-submenu, details .details-content ul, details ul ul') ||
        (link.closest('details, [data-trigger-id]') && !link.closest('summary')),
      );

      pushEvent('nav_click', {
        navLocation: 'header',
        navLinkName: link.textContent?.trim(),
        navLinkUrl: href,
        navLevel: isL2 ? 'l2' : 'l1',
        navSection: getHeaderNavSection(/** @type {HTMLElement} */ (link)),
      });
      return;
    }

    // ── Footer nav ──────────────────────────────────────────────────────────
    const footer = link.closest('footer');
    if (footer) {
      // Footer menus render as <details class="menu__details"> with the group
      // heading in .menu__heading and links in .details-content.
      const navSection =
        link
          .closest('.menu__details, [class*="footer-block"], [class*="footer__column"]')
          ?.querySelector('.menu__heading__default, h3, h4, p, strong')
          ?.textContent?.trim() || '';

      pushEvent('nav_click', {
        navLocation: 'footer',
        navLevel: 'l1',
        navLinkName: link.textContent?.trim(),
        navLinkUrl: href,
        navSection: (navSection || '').replace(/\s+/g, ' ').trim() || undefined,
      });
    }
  });
}

/**
 * Visible text of an element as one clean line: <br> counts as a space (so
 * "Uncompromising Quality<br>from Start" doesn't collapse to "Qualityfrom"),
 * and button text (e.g. a rich-text "Read more" toggle) is left out.
 * @param {Element} el
 * @returns {string}
 */
function readableText(el) {
  let out = '';
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = /** @type {Element} */ (node).tagName;
        if (tag === 'BUTTON' || tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') return NodeFilter.FILTER_REJECT;
        return tag === 'BR' ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    out += node.nodeType === Node.ELEMENT_NODE ? ' ' : node.textContent;
  }
  return out.replace(/\s+/g, ' ').trim();
}

/**
 * Text of a clicked module link/button, for moduleLinkText. Carousel and blog
 * cards are image links or full-card overlay links whose only text is a
 * screen-reader label, and arrow/CTA buttons are often icon-only — readableText
 * returns '' for those, which is why carousel/blog clicks reached the dataLayer
 * with an empty moduleLinkText (UAT Defects row 66). Fall back, in order, to the
 * accessible name, the image alt, then the card's heading.
 * @param {Element|null} link
 * @returns {string}
 */
function getLinkText(link) {
  if (!link) return '';
  const visible = readableText(link);
  if (visible) return visible.substring(0, 100);
  const aria = link.getAttribute('aria-label')?.trim() || link.getAttribute('title')?.trim();
  if (aria) return aria.substring(0, 100);
  const alt = link.querySelector('img[alt]')?.getAttribute('alt')?.trim();
  if (alt) return alt.substring(0, 100);
  const card = link.closest('article, li, [class*="card"]');
  const heading = card?.querySelector('h1,h2,h3,h4,h5,[class*="title"],[class*="heading"]');
  const headingText = heading ? readableText(heading) : '';
  return headingText.substring(0, 100);
}

/**
 * Resolve a human-readable module name for a module container.
 * Priority: explicit data attribute → first text block → heading text → image
 * alt → first text node.
 * Hero / split_banner / rich_text / collection_grid content sits in nested
 * group blocks that the Liquid name derivation can't reach, so their
 * data-gtm-module-name renders empty. The first Horizon .text-block is the
 * module's eyebrow/headline ("LAST-MINUTE GIFTS", "gifts from $25, delivered
 * today"); reading it whole avoids the partial first-text-node fragments the
 * tree-walk fallback produced.
 * @param {HTMLElement|null} el
 * @returns {string}
 */
function getModuleName(el) {
  if (!el) return '';
  const fromData = el.dataset?.gtmModuleName;
  if (fromData && fromData.trim()) return fromData.trim();

  const textBlock = el.querySelector('.text-block');
  const blockText = textBlock ? readableText(textBlock) : '';
  if (blockText) return blockText.substring(0, 80);

  const heading = el.querySelector('h1,h2,h3,h4,[class*="title"],[class*="heading"]');
  if (heading?.textContent?.trim()) return heading.textContent.trim().replace(/\s+/g, ' ').substring(0, 80);

  const img = el.querySelector('img[alt]');
  const alt = img?.getAttribute('alt')?.trim();
  if (alt) return alt.substring(0, 80);

  // Walk DOM text nodes to find the first visible, non-empty text — covers
  // generic section.liquid wrappers where text lives in arbitrary child blocks
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.textContent?.trim()) return NodeFilter.FILTER_SKIP;
      const tag = node.parentElement?.tagName?.toLowerCase();
      if (tag === 'script' || tag === 'style' || tag === 'noscript') return NodeFilter.FILTER_SKIP;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const firstNode = walker.nextNode();
  const firstText = firstNode?.textContent?.trim();
  if (firstText) return firstText.replace(/\s+/g, ' ').substring(0, 80);

  return '';
}

/* ─────────────────────────────────────────────────────────────────────────────
   3. MODULE POSITIONS
   Assigns numeric 1-based positions to all GTM module elements based on their
   visible DOM order within .shopify-section wrappers. Runs once at init so
   module_click and module_view both read consistent numeric positions.
   Sections hidden at the current breakpoint (offsetHeight === 0 and
   offsetWidth === 0, i.e. display:none) are skipped so that a desktop-only
   hero and a mobile-only hero don't both inflate the count.
───────────────────────────────────────────────────────────────────────────── */

/**
 * True for a module/section that renders nothing a shopper could see.
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isEmptyModule(el) {
  if (el.offsetHeight > 0) return false;
  if (el.querySelector('img, video, iframe, picture, svg')) return false;
  return !readableText(el);
}

/**
 * Whether a module is really on screen for the shopper. IntersectionObserver
 * only checks geometry: a mega-menu promo inside a closed menu or the hidden
 * mobile drawer still "intersects" when the panel is laid out with
 * visibility:hidden / opacity:0, which fired mega_menu_promo impressions on
 * page load (UAT Defects row 66).
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isActuallyVisible(el) {
  if (!el.isConnected || el.offsetHeight === 0) return false;
  if (typeof el.checkVisibility === 'function') {
    // Both spellings: opacityProperty/visibilityProperty (current spec) and
    // checkOpacity/checkVisibilityCSS (Chrome 105–120, Safari 17.4).
    return el.checkVisibility({
      opacityProperty: true,
      visibilityProperty: true,
      checkOpacity: true,
      checkVisibilityCSS: true,
    });
  }
  for (let n = /** @type {HTMLElement|null} */ (el); n && n !== document.body; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0) return false;
  }
  return true;
}

function assignModulePositions() {
  const sections = /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('.shopify-section')));
  let pos = 1;
  sections.forEach((section) => {
    if (section.closest('#header-group')) return;
    if (!section.offsetHeight && !section.offsetWidth) return;
    // Empty sections (e.g. rich_text with no content: 0px tall, no text, no media)
    // are not modules — counting them gave unnamed impressions at positions 9/10
    // and shifted every module after them (UAT Defects row 66).
    if (isEmptyModule(section)) return;
    const moduleEl = /** @type {HTMLElement | null} */ (section.querySelector('[data-gtm-module-type], announcement-bar-component'));
    if (moduleEl) moduleEl.dataset.gtmModulePosition = String(pos);
    pos++;
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   4. MODULE CLICK
   Covers: skinny/announcement banner, hero CTA, mega-menu promo tiles,
   and any element with data-gtm-module-type attribute.
───────────────────────────────────────────────────────────────────────────── */

/**
 * Text of the announcement bar's current slide. A linked slide repeats its text in
 * a visually-hidden span inside the overlay link (blocks/_announcement.liquid), so
 * the slide's whole textContent read "Shop our latest arrivals! Shop our latest
 * arrivals!" (Anatta defects 2026-10-27). Only the visible `.announcement-bar__text`
 * of the active slide is read; inactive slides are aria-hidden="true".
 * @param {Element} bar
 * @returns {string}
 */
function announcementSlideText(bar) {
  const text = bar.querySelector('[aria-hidden="false"] .announcement-bar__text') || bar.querySelector('.announcement-bar__text');
  return text ? readableText(text).substring(0, 100) : '';
}

/**
 * The link a module click is about. Most clicks land on (or inside) the link
 * itself; a click on a card's non-link area (a blog card's title/excerpt — its
 * only link is the image overlay) falls back to that card's own link, so
 * moduleLinkText / moduleLinkUrl are populated instead of '' (Anatta defects
 * 2026-10-27, blog_carousel). Never reaches outside the module.
 * @param {HTMLElement} target
 * @param {Element} moduleEl
 * @returns {Element|null}
 */
function moduleClickLink(target, moduleEl) {
  const direct = target.closest('a[href]') || target.closest('button');
  if (direct && moduleEl.contains(direct)) return direct;
  const card = target.closest('.blog-article-card, .featured-blog-posts-card, .product-card, .resource-list__item, article, li');
  if (card && moduleEl.contains(card)) return card.querySelector('a[href]');
  return null;
}

function initModuleClicks() {
  // CAPTURE phase. Product-card "Shop now" buttons are intercepted by the
  // personalization drawer's document click listener, which calls
  // stopImmediatePropagation() — a bubble-phase listener registered after it never
  // ran, so product_carousel "Shop now" clicks sent no module_click (Anatta defects
  // 2026-10-27). Capturing on document runs before any bubble handler can stop it.
  /** @param {MouseEvent} e */
  const onModuleClick = (e) => {
    const target = /** @type {HTMLElement} */ (e.target);
    if (!(target instanceof Element)) return;

    // ── Hero CTA (checked before generic handler — hero also has data-gtm-module-type) ──
    const heroSection = target.closest('.hero');
    if (heroSection) {
      // Exclude nav overlay clicks (header overlaps hero; "Shop All" menu open fires false hero clicks)
      if (target.closest('header-component')) return;
      const link = target.closest('a[href]') || target.closest('button');
      if (!link) return;
      // Same value as moduleLinkText, so image-only / aria-labelled hero links
      // still record; a link with no text, label, alt or heading is a ghost click.
      const linkText = getLinkText(link);
      if (!linkText) return;
      pushEvent('module_click', {
        moduleType: 'hero',
        moduleName: getModuleName(/** @type {HTMLElement} */ (heroSection)) || 'hero',
        modulePosition: heroSection.dataset?.gtmModulePosition || 'hero',
        modulePageType: window.EAPageData?.pageType || '',
        moduleLinkText: linkText,
        moduleLinkUrl: /** @type {HTMLAnchorElement} */ (link)?.href || '',
      });
      return;
    }

    // ── Generic: elements decorated with data-gtm-module-type ──────────────
    const gtmEl = target.closest('[data-gtm-module-type]');
    if (gtmEl) {
      const link = moduleClickLink(target, gtmEl);
      // A click on bare module background (no link, button or card) is not a
      // module click — it only produced events with an empty link text and URL.
      if (!link) return;
      const linkText = getLinkText(link);
      // Collection tabs are ONE module (the section); the clicked tab's title is
      // the more specific name when the click lands inside a tab panel.
      const tabTitle = /** @type {HTMLElement|null} */ (target.closest('[data-tab-title]'))?.dataset.tabTitle?.trim();
      pushEvent('module_click', {
        moduleType: gtmEl.dataset.gtmModuleType,
        moduleName: tabTitle || getModuleName(/** @type {HTMLElement} */ (gtmEl)) || linkText,
        modulePosition: gtmEl.dataset.gtmModulePosition || '',
        modulePageType: window.EAPageData?.pageType || '',
        moduleLinkText: linkText,
        moduleLinkUrl: /** @type {HTMLAnchorElement} */ (link)?.href || '',
      });
      return;
    }

    // ── Skinny / announcement banner ───────────────────────────────────────
    const announcementBar = target.closest('announcement-bar-component');
    if (announcementBar) {
      // A click on an anchor keeps that anchor; anything else (e.g. the slide
      // arrows) resolves to the link of the slide showing when it was clicked.
      const link = target.closest('a[href]') || announcementBar.querySelector('[aria-hidden="false"] a[href]') || null;
      pushEvent('module_click', {
        moduleType: 'skinny_banner',
        moduleName: announcementSlideText(announcementBar) || 'announcement',
        modulePosition: 'top',
        modulePageType: window.EAPageData?.pageType || '',
        moduleLinkText: getLinkText(link),
        moduleLinkUrl: /** @type {HTMLAnchorElement} */ (link)?.href || '',
      });
      return;
    }
  };
  document.addEventListener('click', onModuleClick, true);
}

/* ─────────────────────────────────────────────────────────────────────────────
   4. MODULE VIEW (impressions)
   Uses IntersectionObserver — fires once per element per page load.
   Watches: announcement bar, hero, and [data-gtm-module-type] elements.
───────────────────────────────────────────────────────────────────────────── */

function initModuleViews() {
  if (!('IntersectionObserver' in window)) return;

  /** Elements that intersected while not actually visible — re-checked on menu/drawer changes. */
  const pendingHidden = /** @type {Set<HTMLElement>} */ (new Set());

  /** @param {HTMLElement} el */
  const impress = (el) => {
    if (el.dataset.gtmModuleImpressed) return; // fire only once
    el.dataset.gtmModuleImpressed = 'true';
    pendingHidden.delete(el);
    pushEvent('module_view', {
      moduleType: el.dataset.gtmModuleType || el.dataset.gtmModuleAutoType || '',
      moduleName: getModuleName(el),
      modulePosition: el.dataset.gtmModulePosition || '',
      modulePageType: window.EAPageData?.pageType || '',
    });
    observer.unobserve(el);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = /** @type {HTMLElement} */ (entry.target);
        if (!entry.isIntersecting) {
          pendingHidden.delete(el);
          return;
        }
        if (el.dataset.gtmModuleImpressed) return;
        // Empty module (0px, no text/media): never an impression.
        if (isEmptyModule(el)) return;
        if (!isActuallyVisible(el)) {
          // In the viewport but hidden (closed mega menu / drawer) — wait for it
          // to be revealed instead of counting it now.
          pendingHidden.add(el);
          return;
        }
        impress(el);
      });
    },
    { threshold: 0.5 },
  );

  // A hidden-but-intersecting module (mega-menu promo) becomes visible without
  // any geometry change, so IntersectionObserver won't call back again. Re-check
  // the waiting ones when a menu/drawer opens or finishes animating in.
  let recheckTimer = 0;
  const recheckPending = () => {
    if (!pendingHidden.size) return;
    clearTimeout(recheckTimer);
    recheckTimer = window.setTimeout(() => {
      pendingHidden.forEach((el) => {
        if (el.dataset.gtmModuleImpressed) return pendingHidden.delete(el);
        // Same bar as the observer's threshold: at least 50% of the module on screen.
        const r = el.getBoundingClientRect();
        const visibleW = Math.min(r.right, window.innerWidth) - Math.max(r.left, 0);
        const visibleH = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
        const area = r.width * r.height;
        const ratio = area > 0 && visibleW > 0 && visibleH > 0 ? (visibleW * visibleH) / area : 0;
        if (ratio >= 0.5 && isActuallyVisible(el)) impress(el);
      });
    }, 150);
  };
  document.addEventListener('toggle', recheckPending, true); // <details> menus (toggle doesn't bubble)
  document.addEventListener('transitionend', recheckPending, true); // drawers / fade-ins
  document.addEventListener('animationend', recheckPending, true); // keyframe reveals
  document.addEventListener('mouseover', (e) => {
    if (/** @type {HTMLElement} */ (e.target)?.closest?.('header-component, .js-nav-trigger')) recheckPending();
  });

  // Auto-observe known module containers
  document.querySelectorAll('announcement-bar-component').forEach((el) => {
    /** @type {HTMLElement} */ (el).dataset.gtmModuleAutoType = 'skinny_banner';
    if (!(/** @type {HTMLElement} */ (el).dataset.gtmModulePosition)) {
      /** @type {HTMLElement} */ (el).dataset.gtmModulePosition = 'top';
    }
    observer.observe(el);
  });

  document.querySelectorAll('.hero').forEach((el) => {
    /** @type {HTMLElement} */ (el).dataset.gtmModuleAutoType = 'hero';
    observer.observe(el);
  });

  // Any section decorated with data-gtm-module-type by Liquid
  document.querySelectorAll('[data-gtm-module-type]').forEach((el) => observer.observe(el));
}

/* ─────────────────────────────────────────────────────────────────────────────
   5. FILTER APPLY
   Listens to the theme's filter:update custom event dispatched by facets.js.
   Fires one push per filter dimension changed.
───────────────────────────────────────────────────────────────────────────── */

function initFilterApply() {
  // Dedup signature shared by both trigger paths (the theme's filter:update
  // event and the History-API fallback below) so a single filter change never
  // produces duplicate filter_apply pushes.
  let lastStateSig = null;

  // Human-readable labels for Shopify's raw sort keys.
  const SORT_LABELS = {
    'price-ascending': 'Price: Low to High',
    'price-descending': 'Price: High to Low',
    'best-selling': 'Best Selling',
    'created-descending': 'Date: New to Old',
    'created-ascending': 'Date: Old to New',
    'title-ascending': 'A-Z',
    'title-descending': 'Z-A',
    manual: 'Featured',
    'most-relevant': 'Relevance',
  };

  function classifyFilter(key) {
    const k = key.toLowerCase();
    if (k.includes('price')) return 'price';
    if (k.includes('occasion') || k.includes('named_tag') || k.includes('tag')) return 'occasion';
    if (k.includes('sort')) return 'sort';
    return 'category';
  }

  // Build the display range the customer sees, using the store currency symbol
  // (window.EAPageData.currencySymbol, from shop.money_format). Params are in
  // the shop's major currency units. e.g. gte=30,lte=49 -> "$30 - $49".
  function formatPriceRange(gte, lte) {
    const sym = window.EAPageData?.currencySymbol || '$';
    // Postfix currencies (e.g. "{{amount}} kr.") place the symbol after the
    // amount with a separating space; prefix currencies ($) hug the amount.
    const suffix = window.EAPageData?.currencySymbolSuffix === true;
    const money = (v) => (suffix ? `${String(v).trim()} ${sym}` : `${sym}${String(v).trim()}`);
    if (gte != null && gte !== '' && lte != null && lte !== '') return `${money(gte)} - ${money(lte)}`;
    if (gte != null && gte !== '') return `${money(gte)}+`;
    if (lte != null && lte !== '') return `Under ${money(lte)}`;
    return '';
  }

  // Resolve the human-readable label for a list filter value from the DOM.
  // EA renders filter values as <a data-filter-link data-param-name="..." data-param-value="...">
  //   <span data-check-icon>…</span><span>Human label</span>
  // </a>. Falls back to the raw value (e.g. a GID) when the element is not found.
  function resolveFilterLabel(paramName, paramValue) {
    try {
      const el = document.querySelector(
        `[data-filter-link][data-param-name="${CSS.escape(paramName)}"][data-param-value="${CSS.escape(paramValue)}"]`,
      );
      if (el) {
        const spans = el.querySelectorAll('span');
        const last = spans[spans.length - 1];
        const label = last ? readableText(last) : '';
        if (label) return label;
      }
    } catch (_) {}
    return paramValue;
  }

  // Resolve the human-readable label for an EA preset price range.
  // range is "gte-lte" e.g. "50-75". Looks up [data-price-link] in the DOM.
  // Falls back to formatPriceRange when the element is not found.
  function resolvePriceRangeLabel(range) {
    const dashIdx = range.indexOf('-');
    if (dashIdx < 0) return range;
    const gte = range.slice(0, dashIdx);
    const lte = range.slice(dashIdx + 1);
    try {
      const gteAttr = gte && gte !== '0' ? `[data-price-gte="${CSS.escape(gte)}"]` : '';
      const el = document.querySelector(`[data-price-link]${gteAttr}[data-price-lte="${CSS.escape(lte)}"]`);
      if (el) {
        const spans = el.querySelectorAll('span');
        const last = spans[spans.length - 1];
        if (last && last.textContent.trim()) return last.textContent.trim();
      }
    } catch (_) {}
    return formatPriceRange(gte || null, lte || null);
  }

  // Wait for the collection-viewer to finish its navigation load, then read the
  // current [data-product-count] from the live DOM (not a captured reference).
  // Two-phase: first waits for loading to START (guards against resolving on a
  // prior navigation's end), then waits for loading to END.
  // cv-total-pending is intentionally excluded — it stays set while multi-page
  // hydration refines the total, but the per-filter count is already correct.
  function readResultCountAsync(callback) {
    const sectionEl = document.querySelector('[data-collection-section]');

    function readCount() {
      const el = document.querySelector('[data-product-count], [data-results-count], .results-count');
      if (!el) return undefined;
      const m = (el.textContent || '').replace(/,/g, '').match(/\d+/);
      return m ? Number(m[0]) : undefined;
    }

    let done = false;
    function finish() {
      if (done) return;
      done = true;
      callback(readCount());
    }

    if (!sectionEl) {
      setTimeout(finish, 0);
      return;
    }

    const section = /** @type {Element} */ (sectionEl);

    // cv-nav-loading on the section wrapper; cv-loading on the collection-viewer element.
    // cv-total-pending is deliberately excluded (see note above).
    function isNavLoading() {
      return section.classList.contains('cv-nav-loading') || !!section.querySelector('collection-viewer.cv-loading');
    }

    // Two-phase: wait for loading to start, then wait for it to end.
    // This prevents resolving on a previous navigation's load-end event when
    // sequential filters fire faster than the network round-trip.
    let loadingStarted = isNavLoading();

    const mo = new MutationObserver(() => {
      if (!loadingStarted) {
        if (isNavLoading()) loadingStarted = true;
      } else if (!isNavLoading()) {
        mo.disconnect();
        setTimeout(finish, 50);
      }
    });
    mo.observe(section, { attributes: true, attributeFilter: ['class'], subtree: true });

    // Fallback: fires if loading never starts (client-side filters, instant
    // cached response) or never ends within a reasonable window.
    setTimeout(() => {
      mo.disconnect();
      finish();
    }, 3000);
  }

  // Previous filter state for diffing — tracks what was active before the current
  // change so we emit only the filter that changed and the correct action.
  // hasPriceFilter / priceGte / priceLte are only for the standard price slider
  // (no price_ranges preset). Preset price ranges live in filters under 'price_ranges'.
  let lastFilterState =
    /** @type {{sort: string|null, filters: Map<string, string[]>, hasPriceFilter: boolean, priceGte: string|null, priceLte: string|null}|null} */ (
      null
    );

  /**
   * Emit filter_apply for the current filter state, deduped on a signature of
   * the sort + filter.* params so repeated identical states are ignored.
   * Diffs against lastFilterState to emit ONLY the changed filter and the correct
   * action (apply | remove). resultCount is read after a short defer.
   * @param {URLSearchParams} params
   */
  function handleFilterState(params) {
    if (!params) return;
    const pageType = window.EAPageData?.pageType || 'plp';

    const sortValue = params.get('sort_by') || null;
    const filterEntries = [...params.entries()].filter(([key]) => key.startsWith('filter.'));

    const priceRangesRaw = params.get('price_ranges') || '';
    const priceRangesList = priceRangesRaw
      ? priceRangesRaw
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean)
      : [];

    // Standard price slider only when no preset ranges are active
    const priceGte = priceRangesList.length === 0 ? params.get('filter.v.price.gte') : null;
    const priceLte = priceRangesList.length === 0 ? params.get('filter.v.price.lte') : null;

    const stateSig = JSON.stringify([sortValue || '', filterEntries, priceRangesRaw]);

    // TEMP(UAT DEBUG): trace every filter-state evaluation so we can see whether
    // a price change reaches here, what the price params are, and if the dedup
    // swallowed it. REMOVE after UAT.
    console.log('%c[EA DataLayer] filter-state', 'color:#4fc3f7;font-weight:bold', {
      params: params.toString(),
      sortValue,
      priceGte,
      priceLte,
      filterKeys: filterEntries.map(([k]) => k),
      deduped: stateSig === lastStateSig,
    });

    if (stateSig === lastStateSig) return; // no net change — avoid double fire
    lastStateSig = stateSig;

    // Build a map of non-price filter key → [values] for the new state.
    // Preset price ranges are stored under the synthetic key 'price_ranges'.
    const newFilters = /** @type {Map<string, string[]>} */ (new Map());
    filterEntries
      .filter(([key]) => !key.startsWith('filter.v.price.'))
      .forEach(([key, value]) => {
        const vals = newFilters.get(key) || [];
        vals.push(value);
        newFilters.set(key, vals);
      });
    if (priceRangesList.length > 0) {
      newFilters.set('price_ranges', priceRangesList);
    }

    const prevState = lastFilterState;
    const hasPrice = priceGte != null || priceLte != null;

    // Update lastFilterState synchronously so back-to-back filter changes each
    // see the correct previous state, without waiting for the async count read.
    lastFilterState = {
      sort: sortValue,
      filters: newFilters,
      hasPriceFilter: hasPrice,
      priceGte,
      priceLte,
    };

    // Wait for [data-product-count] to reflect the post-filter result set before
    // emitting. MutationObserver detects the exact moment the count updates, so
    // the value is always the filtered count regardless of network speed.
    readResultCountAsync((resultCount) => {
      const withCount = (payload) => (resultCount != null ? { ...payload, resultCount } : payload);

      // ── Sort ──
      const prevSort = prevState?.sort || null;
      if (sortValue !== prevSort) {
        if (sortValue) {
          pushEvent(
            'filter_apply',
            withCount({ filterType: 'sort', filterValue: SORT_LABELS[sortValue] || sortValue, filterAction: 'apply', pageType }),
          );
        } else if (prevSort) {
          pushEvent(
            'filter_apply',
            withCount({ filterType: 'sort', filterValue: SORT_LABELS[prevSort] || prevSort, filterAction: 'remove', pageType }),
          );
        }
      }

      // ── Price (standard slider only — preset ranges handled below via price_ranges) ──
      const hadPrice = prevState?.hasPriceFilter || false;
      const priceChanged = priceGte !== prevState?.priceGte || priceLte !== prevState?.priceLte;
      if (hasPrice && (!hadPrice || priceChanged)) {
        if (!hadPrice) {
          pushEvent(
            'filter_apply',
            withCount({ filterType: 'price', filterValue: formatPriceRange(priceGte, priceLte), filterAction: 'apply', pageType }),
          );
        } else {
          pushEvent(
            'filter_apply',
            withCount({
              filterType: 'price',
              filterValue: formatPriceRange(prevState.priceGte, prevState.priceLte),
              filterAction: 'remove',
              pageType,
            }),
          );
          pushEvent(
            'filter_apply',
            withCount({ filterType: 'price', filterValue: formatPriceRange(priceGte, priceLte), filterAction: 'apply', pageType }),
          );
        }
      } else if (!hasPrice && hadPrice) {
        pushEvent(
          'filter_apply',
          withCount({
            filterType: 'price',
            filterValue: formatPriceRange(prevState?.priceGte, prevState?.priceLte),
            filterAction: 'remove',
            pageType,
          }),
        );
      }

      // ── Non-price filters: value-by-value diff ──
      // Each filter key can have multiple selected values (e.g. two occasion tags).
      // Comparing whole arrays would fire 'apply' for remaining values when one is
      // removed. Instead: one event per added value ('apply') and per removed value ('remove').
      const prevFilters = prevState?.filters || new Map();
      const allFilterKeys = new Set([...prevFilters.keys(), ...newFilters.keys()]);

      allFilterKeys.forEach((key) => {
        const prevVals = new Set(prevFilters.get(key) || []);
        const newVals = new Set(newFilters.get(key) || []);
        const isPriceRange = key === 'price_ranges';
        const type = isPriceRange ? 'price' : classifyFilter(key);

        newVals.forEach((v) => {
          if (!prevVals.has(v)) {
            const filterValue = isPriceRange ? resolvePriceRangeLabel(v) : resolveFilterLabel(key, v);
            pushEvent('filter_apply', withCount({ filterType: type, filterValue, filterAction: 'apply', pageType }));
          }
        });

        prevVals.forEach((v) => {
          if (!newVals.has(v)) {
            const filterValue = isPriceRange ? resolvePriceRangeLabel(v) : resolveFilterLabel(key, v);
            pushEvent('filter_apply', withCount({ filterType: type, filterValue, filterAction: 'remove', pageType }));
          }
        });
      });
    });
  }

  // Path 1 — the theme's facet components dispatch filter:update (facets.js).
  // We defer and read the full URL instead of using e.detail?.queryParams, because
  // each facet component only includes its own params — omitting other active
  // filters and causing incorrect diffs. pushState runs synchronously inside the
  // same facet handler that dispatched this event, so the URL is already updated
  // by the time our deferred callback fires. Dedup against Path 2 prevents
  // double-firing when both paths pick up the same navigation.
  document.addEventListener('filter:update', () => {
    setTimeout(() => handleFilterState(new URLSearchParams(window.location.search)), 0);
  });

  // Path 2 — Catches filters that update the URL without dispatching filter:update
  // (e.g. the price slider) and serves as a reliable fallback for everything else.
  const pageType = window.EAPageData?.pageType;
  if (pageType === 'plp' || pageType === 'search') {
    const fireFromUrl = () => handleFilterState(new URLSearchParams(window.location.search));

    const wrap = (original) =>
      function (...args) {
        const result = original.apply(this, args);
        // Defer so the URL is fully updated before we read it.
        setTimeout(fireFromUrl, 0);
        return result;
      };
    history.pushState = wrap(history.pushState);
    history.replaceState = wrap(history.replaceState);
    window.addEventListener('popstate', fireFromUrl);

    // Seed both lastStateSig AND lastFilterState from the landing URL so
    // pre-applied filters (navigating back from PDP, shared filtered URL, etc.)
    // don't appear as a fresh "apply" on the user's first interaction.
    lastStateSig = (() => {
      const params = new URLSearchParams(window.location.search);
      const sortValue = params.get('sort_by') || null;
      const filterEntries = [...params.entries()].filter(([key]) => key.startsWith('filter.'));
      const priceRangesRaw = params.get('price_ranges') || '';
      const priceRangesList = priceRangesRaw
        ? priceRangesRaw
            .split(',')
            .map((r) => r.trim())
            .filter(Boolean)
        : [];
      const priceGte = priceRangesList.length === 0 ? params.get('filter.v.price.gte') : null;
      const priceLte = priceRangesList.length === 0 ? params.get('filter.v.price.lte') : null;

      const seedFilters = /** @type {Map<string, string[]>} */ (new Map());
      filterEntries
        .filter(([key]) => !key.startsWith('filter.v.price.'))
        .forEach(([key, value]) => {
          const vals = seedFilters.get(key) || [];
          vals.push(value);
          seedFilters.set(key, vals);
        });
      if (priceRangesList.length > 0) {
        seedFilters.set('price_ranges', priceRangesList);
      }

      lastFilterState = {
        sort: sortValue,
        filters: seedFilters,
        hasPriceFilter: priceGte != null || priceLte != null,
        priceGte,
        priceLte,
      };

      return JSON.stringify([sortValue || '', filterEntries, priceRangesRaw]);
    })();
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   6. SEARCH
   a) Keyword submit — form with action containing /search
   b) Predictive — MutationObserver on the search results container
   c) Null results → also fires site_error
───────────────────────────────────────────────────────────────────────────── */

// Query that was just submitted as a full-text search. Used so the predictive
// observer doesn't re-report the same term as 'predictive' in the brief moment
// before the browser navigates to the /search results page.
let lastSubmittedSearch = '';

// Best-effort SKU/id list from a set of result cards (optional searchSKUResults).
// Prefers a real SKU attribute, falls back to product id when SKU isn't in DOM.
function collectSkus(cards) {
  return [...cards]
    .map(
      (c) =>
        c.getAttribute?.('data-sku') ||
        c.querySelector?.('[data-sku]')?.getAttribute('data-sku') ||
        c.getAttribute?.('data-product-id') ||
        c.querySelector?.('[data-product-id]')?.getAttribute('data-product-id') ||
        '',
    )
    .filter(Boolean);
}

function initSearch() {
  const observedContainers = new Set();
  let lastPredictiveQuery = '';

  // ── Keyword submit ──────────────────────────────────────────────────────
  // A full-text submit navigates to the /search results page, where
  // initSearchResults() fires the authoritative 'keyword' event *with* a real
  // result count (from Liquid). So here we only record the term to suppress a
  // duplicate 'predictive' event — we intentionally do not push a keyword event
  // with a null count (that was the source of the empty searchCount).
  document.addEventListener('submit', (e) => {
    const form = /** @type {HTMLFormElement} */ (e.target.closest('form'));
    if (!form) return;
    const action = form.action || '';
    if (!action.includes('/search') && !form.querySelector('input[name="q"]')) return;

    const q = /** @type {HTMLInputElement} */ (form.querySelector('input[name="q"]'))?.value?.trim();
    if (!q) return;

    lastSubmittedSearch = q;
    lastPredictiveQuery = q;

    // Only suppress the duplicate 'predictive' event during the brief window
    // before the browser navigates to /search. If navigation doesn't tear down
    // this script (drawer-based search, SPA-style nav, or a cancelled submit),
    // clear the flag so the same term can fire a predictive event again later.
    setTimeout(() => {
      if (lastSubmittedSearch === q) lastSubmittedSearch = '';
    }, 2000);
  });

  // ── Predictive results observer ─────────────────────────────────────────
  // Search UIs that can render results: the React predictive app
  // (.ps-root[data-predictive-search] / #predictive-search-results-panel), the
  // server-rendered predictive snippet (#predictive-search-results), and the
  // native header search drawer (blocks/_search-drawer.liquid), which injects
  // section HTML into [data-search-results]. A page can expose more than one at
  // once (e.g. a global search modal AND the header drawer), so observe EVERY
  // matching root, not just the first. lastPredictiveQuery is shared across all
  // observers, so the same settled query can't double-fire from two containers.
  const PREDICTIVE_CONTAINERS =
    '[data-predictive-search], #predictive-search-results-panel, #predictive-search-results, .predictive-search-dropdown, #search-drawer, .js-search-drawer [data-search-results]';

  /** @param {HTMLElement} container */
  function observeContainer(container) {
    if (observedContainers.has(container)) return;
    observedContainers.add(container);

    // Predictive results mutate several times per keystroke (clear → loading →
    // settled). Debounce so we only act once the DOM has settled, and don't
    // record/dedupe a query until we emit its final state — otherwise a
    // transient loading frame (0 cards, no "no results" marker) would fire a
    // premature null-results and block the settled emission.
    let settleTimer = null;
    const observer = new MutationObserver(() => {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const input = /** @type {HTMLInputElement|null} */ (document.querySelector('input[name="q"], [data-search-input]'));
        const q = input?.value?.trim();
        if (!q || q.length < 2 || q === lastSubmittedSearch) return;
        if (q === lastPredictiveQuery) return; // already emitted this settled query

        // Count real product result cards from any predictive UI, excluding
        // the "recently viewed" suggestions that share the server card class.
        const productItems = [
          ...container.querySelectorAll('.ps-product-card, .predictive-search-results__card--product, .product-card[data-product-id]'),
        ].filter((card) => !card.closest('.recently-viewed-wrapper'));
        const hasNoResults =
          container.querySelector('.ps-no-results, .predictive-search-results__no-results, .search-drawer-results__no-results') !== null;
        const count = productItems.length;

        // Still loading: no products AND no explicit "no results" marker yet.
        // Wait for a later mutation to settle before emitting/recording.
        if (count === 0 && !hasNoResults) return;

        lastPredictiveQuery = q; // record only when emitting the final state
        const skus = collectSkus(productItems);

        // searchType per spec enum: 0 results => 'null-results', else predictive.
        pushEvent('search', {
          searchValue: q,
          searchType: count === 0 || hasNoResults ? 'null-results' : 'predictive',
          searchCount: count,
          ...(skus.length ? { searchSKUResults: skus } : {}),
        });

        if (count === 0 || hasNoResults) {
          pushEvent('site_error', {
            errorType: 'null-search',
            errorMessage: `No results for: ${q}`,
            pageType: window.EAPageData?.pageType || 'search',
          });
        }
      }, 350);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  function attachPredictiveObserver() {
    document.querySelectorAll(PREDICTIVE_CONTAINERS).forEach((el) => observeContainer(/** @type {HTMLElement} */ (el)));
  }

  // Attach observer when user opens search
  document.addEventListener('click', (e) => {
    if (/** @type {HTMLElement} */ (e.target).closest('.header__icon--search, [aria-controls*="search"], [data-open-search]')) {
      setTimeout(attachPredictiveObserver, 200);
    }
  });

  // Also try immediately in case search drawer is already in DOM
  attachPredictiveObserver();
}

/**
 * Authoritative full-text search event, fired on the /search results page.
 * searchType is 'keyword' and searchCount comes from Liquid (search.results_count),
 * so the submitted-search event is correctly typed and always carries a count.
 */
function initSearchResults() {
  const d = window.EAPageData;
  if (!d || d.pageType !== 'search') return;
  const q = (d.searchTerm || '').trim();
  if (!q) return;

  const count = Number.isFinite(d.searchResultsCount) ? d.searchResultsCount : null;

  // Optional: SKUs/ids of the products rendered on the results page.
  const skus = collectSkus(document.querySelectorAll('.product-card[data-product-id], [data-product-grid] [data-product-id]'));

  // searchType per spec enum: 0 results => 'null-results', otherwise 'keyword'.
  pushEvent('search', {
    searchValue: q,
    searchType: count === 0 ? 'null-results' : 'keyword',
    searchCount: count,
    ...(skus.length ? { searchSKUResults: skus } : {}),
  });

  if (count === 0) {
    pushEvent('site_error', {
      errorType: 'null-search',
      errorMessage: `No results for: ${q}`,
      pageType: 'search',
    });
  }
}

/**
 * Category-browse search event — fired once on every collection/PLP page load.
 * Per the spec, browsing a category is a form of search: searchType
 * 'category-browse', searchValue = collection title, searchCount = product count.
 */
function initCategoryBrowse() {
  const d = window.EAPageData;
  if (!d || d.pageType !== 'plp') return;

  const count = Number.isFinite(d.collectionProductCount) ? d.collectionProductCount : null;

  pushEvent('search', {
    searchValue: d.collectionTitle || '',
    searchType: 'category-browse',
    searchCount: count,
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   7. CART ADD
   Listens to the theme's cart:update custom event dispatched by product-form.js
   and ea-cart-drawer.js. Only fires for successful additions (not errors).
───────────────────────────────────────────────────────────────────────────── */

// cart:update sources that represent a genuine add-to-cart (not qty-change/remove).
// EA add-to-cart flows dispatch from several components: the standard product
// form (PDP + PLP quick-add), the BYOB bundle builder, the personalization
// drawer (size/delivery/card-message picker — the primary PDP add path for most
// EA products), and quick-order quantity increases. All of these were not being
// counted before, which is why cart_add appeared to never fire.
const CART_ADD_SOURCES = ['product-form-component', 'ea-bundle-builder-drawer', 'personalization-drawer', 'quick-order-quantity'];

/**
 * Best-effort addToCartLocation for document-dispatched adds where no
 * originating element is available (personalization drawer). Maps the current
 * page type to a surface label so non-PDP adds aren't all reported as 'plp'.
 * @param {string|undefined} pageType
 * @returns {string}
 */
function addLocationFromPageType(pageType) {
  switch (pageType) {
    case 'pdp':
      return 'pdp';
    case 'plp':
      return 'plp';
    case 'search':
      return 'search';
    case 'homepage':
      return 'homepage';
    case 'cart':
      return 'cart';
    default:
      return pageType || 'other';
  }
}

/**
 * The shopper's current fulfilment context, read from the saved store-availability
 * selection (the single writer is store-availability.js; window.getStoreAvailability
 * is its synchronous reader, localStorage `ea-store-availability` the fallback when
 * the header component hasn't booted yet). Feeds cart_add's zipCode / serviceable /
 * deliveryMethod (UAT Segment "Product Added", Defects row 57).
 * @returns {{zipCode?:string, serviceable?:boolean, deliveryMethod?:string}}
 */
function getFulfillmentContext() {
  /** @type {any} */
  let sel = null;
  try {
    sel = typeof window.getStoreAvailability === 'function' ? window.getStoreAvailability() : null;
    if (!sel) sel = JSON.parse(localStorage.getItem('ea-store-availability') || 'null');
  } catch (_) {
    sel = null;
  }
  if (!sel || typeof sel !== 'object' || !sel.zipCode) return {};

  // Serviceable = the area offers at least one channel. A saved selection with a
  // store (or national shipping) but no availability object is serviceable too —
  // the popup only lets a shopper save a zip that something can fulfil.
  const av = sel.availability;
  const hasChannels = av && (av.delivery !== undefined || av.pickup !== undefined || av.shipment !== undefined);
  const serviceable = hasChannels
    ? av.delivery === true || av.pickup === true || av.shipment === true
    : Boolean(sel.storeId || sel.fulfillmentType === 'shipment');

  return {
    zipCode: String(sel.zipCode),
    serviceable,
    ...(sel.fulfillmentType ? { deliveryMethod: String(sel.fulfillmentType) } : {}),
  };
}

/* ── arrangementId ──────────────────────────────────────────────────────────
   Every product object in the dataLayer carries `arrangementId`: the product's
   `ediblearrangement.arrangement_id` metafield WITHOUT its market prefix
   ("US-4567" → "4567", "CA-861563" → "861563"), as a string. The key is OMITTED
   when the product has no arrangement id. Sources:
     • PDP            — EAPageData.productArrangementId (theme.liquid)
     • forms / cards  — data-arrangement-id (buy-buttons, add-to-cart-button,
                        product-card, product-card-atc, bb-product-card…)
     • cart lines     — the `_arrangement_id` line property (stamped by the
                        personalization drawer, product-form.js and the BYOB drawer)
───────────────────────────────────────────────────────────────────────────── */

/**
 * Strip the market prefix from a raw arrangement_id value.
 * @param {unknown} raw e.g. "US-4567"
 * @returns {string} e.g. "4567" ('' when absent)
 */
function stripArrangementPrefix(raw) {
  // Digit tail, not a fixed 2-letter strip: on a non-English page the prefix arrives
  // translated ("É.-U.-4567", "États-Unis-4567" — EA-4392).
  return String(raw ?? '')
    .trim()
    .replace(/^.*-(?=\d+$)/, '');
}

/**
 * `{ arrangementId }` to spread into a product object, or `{}` when there is none.
 * @param {unknown} raw
 * @returns {{arrangementId?: string}}
 */
function arrangementIdField(raw) {
  const id = stripArrangementPrefix(raw);
  return id ? { arrangementId: id } : {};
}

/**
 * Raw arrangement id for a product form / product card element: the element's own
 * data-arrangement-id, else a descendant's (quick-add renders it on the inner
 * add-to-cart-component), else an ancestor's (the product card around a quick-add
 * form). Blank attributes are skipped.
 * @param {Element|null|undefined} el
 * @returns {string}
 */
function arrangementIdFromElement(el) {
  if (!el) return '';
  const candidates = [el, el.querySelector?.('[data-arrangement-id]'), el.parentElement?.closest?.('[data-arrangement-id]')];
  for (const c of candidates) {
    const v = c?.getAttribute?.('data-arrangement-id')?.trim();
    if (v) return v;
  }
  return '';
}

/**
 * Arrangement id field for a /cart.js line (from its `_arrangement_id` property).
 * @param {any} it
 * @returns {{arrangementId?: string}}
 */
function arrangementIdFromLine(it) {
  return arrangementIdField(it?.properties?._arrangement_id);
}

/**
 * Human variant label. Single-variant products report Shopify's placeholder
 * "Default Title" (form path) or null (cart path) — neither is a variant.
 * @param {unknown} v
 * @returns {string}
 */
function cleanVariant(v) {
  const s = String(v ?? '').trim();
  return s === 'Default Title' || /^\d+$/.test(s) ? '' : s;
}

/**
 * Variant label for a /cart/add.js line. Create-Your-Own boxes are single
 * products whose "size" is the shopper's piece count, carried on the parent line
 * as `_cyo_count` (+ `_cyo_type` {singular, plural}); without it the Create Your
 * Own Box add from the personalization drawer reported variant ''.
 * @param {any} it
 * @returns {string}
 */
function variantFromLine(it) {
  const fromTitle = cleanVariant(it?.variant_title);
  if (fromTitle) return fromTitle;
  const fromOptions = (Array.isArray(it?.options_with_values) ? it.options_with_values : [])
    .map((/** @type {{value?:string}} */ o) => cleanVariant(o?.value))
    .filter(Boolean)
    .join(' / ');
  if (fromOptions) return fromOptions;
  const props = it?.properties || {};
  const count = Number(props._cyo_count);
  if (count > 0) {
    let type = props._cyo_type;
    if (typeof type === 'string') {
      try {
        type = JSON.parse(type);
      } catch (_) {
        /* plain string label */
      }
    }
    const word = typeof type === 'object' && type ? (count === 1 ? type.singular : type.plural) : type;
    return word ? `${count} ${word}` : `${count} count`;
  }
  return '';
}

/**
 * Unit price the shopper actually pays for a cart line, in currency units. Uses
 * the line's final (post line-discount) total, so a BYOB bundle line reports the
 * bundle-discounted price (e.g. 51.00) rather than the list price (59.99) — the
 * same figure as /cart.js final_line_price and the checkout pixel (Anatta defects
 * 2026-10-27, "Bundle products sent at list price"). Falls back to final_price,
 * then to the list price for responses that carry neither.
 * @param {any} it
 * @returns {number}
 */
function paidUnitPrice(it) {
  const qty = Number(it?.quantity) > 0 ? Number(it.quantity) : 1;
  const line = it?.final_line_price != null ? Number(it.final_line_price) : NaN;
  let cents = Number.isFinite(line) ? line / qty : Number(it?.final_price ?? it?.price);
  if (!Number.isFinite(cents)) cents = 0;
  return Math.round(cents) / 100;
}

/**
 * Lines the BYOB bundle builder actually added. The builder dispatches the WHOLE
 * reconciled cart as the event resource (its cart drawer needs it), so reading
 * resource.items reported every line in the cart. It now also passes
 * data.bundleAdded = [{variantId, quantity, key}] — new bundle lines plus quantity
 * increases — and only those are reported, at the added quantity. Lines are
 * matched by cart line `key` (the exact line that grew, priced at its own paid
 * price even if another line shares the variant); variant id is only a fallback. Every bundle
 * line counts, including Step-2 add-ons (`_is_bundle_addon`), which were dropped
 * before (Anatta defects 2026-10-27, "Bundle builder Product Added missing an item").
 * Without bundleAdded (older dispatch) every bundle line in the cart is reported.
 * @param {any} cart
 * @param {Array<{variantId:number|string, quantity:number, key?:string}>|undefined} bundleAdded
 * @returns {any[]}
 */
/**
 * Unit price before discounts, like the checkout pixel's originalPrice (EA-4485 QA).
 * @param {any} it
 * @returns {number}
 */
function listUnitPrice(it) {
  const cents = Number(it?.original_price ?? it?.price);
  return Number.isFinite(cents) ? Math.round(cents) / 100 : 0;
}

/**
 * Tip + store-surcharge line totals in a cart, in cents. They are cart lines, so
 * they sit inside `cart.total_price`, but they are money, not merchandise: the
 * merchandise value is `total_price - cartFeeCents(cart)` — the same rule as
 * merchandiseTotalCents() in assets/cart-contract.js (reimplemented: this file is a
 * plain script with no imports; pinned by tests/gratuity-contract.test.mjs).
 * Cart-level discounts stay applied, since they are already in total_price.
 * @param {any} cart
 * @returns {number}
 */
function cartFeeCents(cart) {
  return (cart?.items || []).reduce((/** @type {number} */ sum, /** @type {any} */ item) => {
    const type = String(item?.properties?.['_item_type'] || '')
      .trim()
      .toLowerCase();
    if (type !== 'tip' && type !== 'surcharge') return sum;
    const price = Number(item?.final_line_price ?? item?.line_price);
    return Number.isFinite(price) && price > 0 ? sum + price : sum;
  }, 0);
}

/**
 * Unrounded paid total of a cart line in cents (final_line_price, else final /
 * list unit price × quantity). Event values sum these and round once, so a line
 * whose unit price does not divide evenly (e.g. 100.00 for 3) is not off by the
 * per-unit rounding.
 * @param {any} it
 * @returns {number}
 */
function paidLineCents(it) {
  const qty = Number(it?.quantity) > 0 ? Number(it.quantity) : 1;
  const line = it?.final_line_price != null ? Number(it.final_line_price) : NaN;
  if (Number.isFinite(line)) return line;
  const unit = Number(it?.final_price ?? it?.price);
  return Number.isFinite(unit) ? unit * qty : 0;
}

/* ── EA-3912 logical lines (bundle-ready arrangements) ─────────────────────────
   One cart line holds the arrangement + its add-ons / upgrades / greeting card /
   gift message in `_ea3912_config`; the Cart Transform expands it into the
   components the order shows as separate line items. /cart.js carries only the
   combined price, so the components and their Transform-set prices are read from
   sections/ea-analytics-cart-components.liquid (Liquid item_components) and each
   becomes its own reported line (EA-4485 QA: drawer add-ons arrived as one
   combined product). */

const CART_COMPONENTS_SECTION = 'ea-analytics-cart-components';

/** Last components seen, by cart line key — product_removed needs them after the line is gone. */
const cartComponentsCache = new Map();

/**
 * Quantity of every cart line (by key) as of the last cart read, kept by the
 * product_removed reconcile. A drawer add copies it BEFORE re-reading the cart, so
 * the added quantity is the delta — /cart/add.js returns the line's new TOTAL
 * quantity, not the amount added.
 * @type {Map<string, number>}
 */
const cartLineQuantities = new Map();

/** @param {any} cart */
function rememberCartQuantities(cart) {
  if (!cart || !Array.isArray(cart.items)) return;
  cartLineQuantities.clear();
  cart.items.forEach((/** @type {any} */ it) => cartLineQuantities.set(it.key, Number(it.quantity) || 0));
}

/**
 * @param {any} it
 * @returns {boolean}
 */
function isLogicalCartLine(it) {
  return Boolean(it?.properties?._ea3912_config);
}

/**
 * Fetch the expanded components of every cart line that has them.
 * Resolves to the shared cache (key -> components[]), or null on failure.
 * @returns {Promise<Map<string, any[]>|null>}
 */
function fetchCartComponents() {
  const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
  return fetch(`${root}?section_id=${CART_COMPONENTS_SECTION}`, { headers: { Accept: 'text/html' } })
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
    .then((html) => {
      const m = /<script[^>]*data-ea-cart-components[^>]*>([\s\S]*?)<\/script>/.exec(html);
      const list = m ? JSON.parse(m[1]) : [];
      (Array.isArray(list) ? list : []).forEach((/** @type {any} */ entry) => {
        if (entry?.key && Array.isArray(entry.components)) cartComponentsCache.set(entry.key, entry.components);
      });
      return cartComponentsCache;
    })
    .catch(() => null);
}

/**
 * Replace each logical line with its components, shaped like cart lines so the
 * existing rules apply unchanged: the arrangement component keeps the parent's
 * properties (`_item_type: parent`, arrangement id …); every other component takes
 * its `_item_type` from the config entry with the same variant, plus the parent's
 * `_add_to_cart_id` (so add-ons get category "Add-on" + parentSku, the free gift
 * message is skipped). A line is only split when its components' paid totals add up
 * to the line's own total — otherwise it is reported as before, never mispriced.
 * @param {any[]} items
 * @param {Map<string, any[]>|null} componentsByKey
 * @returns {any[]}
 */
function expandLogicalLines(items, componentsByKey) {
  return (items || []).flatMap((it) => {
    const comps = isLogicalCartLine(it) && componentsByKey ? componentsByKey.get(it.key) : null;
    if (!Array.isArray(comps) || !comps.length) return [it];
    let config;
    try {
      config = JSON.parse(it.properties._ea3912_config);
    } catch (_) {
      return [it];
    }
    /** @type {Map<string, string>} */
    const typeByVariant = new Map();
    (Array.isArray(config?.items) ? config.items : []).forEach((/** @type {any} */ c) => {
      const id = String(c?.id || '')
        .split('/')
        .pop();
      if (id && c?.t) typeByVariant.set(id, String(c.t));
    });
    // Components are priced for the quantity they were rendered at; scale them to
    // the quantity reported (an add of 1 to a line of 3 reports one set).
    const ratio = it._componentQuantity ? (Number(it.quantity) || 1) / Number(it._componentQuantity) : 1;
    const lineCents = paidLineCents(it);
    const compCents = comps.reduce((sum, c) => sum + paidLineCents(c), 0) * ratio;
    if (Math.abs(compCents - lineCents) > comps.length) return [it];
    return comps.map((c) => {
      const vid = String(c.variant_id);
      const isParent = vid === String(it.variant_id) && !typeByVariant.has(vid);
      const priced = {
        sku: c.sku,
        variant_id: c.variant_id,
        product_title: c.product_title || c.title,
        title: c.title,
        product_type: c.product_type,
        variant_title: c.variant_title,
        quantity: (Number(c.quantity) || 1) * ratio,
        price: c.price,
        original_price: c.original_price,
        final_price: c.final_price,
        final_line_price: Number(c.final_line_price ?? 0) * ratio,
      };
      if (isParent) {
        return {
          ...it,
          ...priced,
          product_type: it.product_type,
          variant_title: it.variant_title,
          options_with_values: it.options_with_values,
          key: it.key,
        };
      }
      return {
        ...priced,
        key: `${it.key}::${vid}`,
        properties: { _item_type: typeByVariant.get(vid) || 'add_on', _add_to_cart_id: it.properties?._add_to_cart_id },
      };
    });
  });
}

/**
 * Cart attribute carrying each logical line's components for the checkout pixel.
 * At checkout the pixel sees a logical line as ONE line at the combined price (the
 * Cart Transform's split is not exposed to Web Pixels), so it cannot report the
 * add-ons on its own (EA-4485 QA, staging W-58769). Analytics-only: nothing reads
 * it for money — the pixel splits a line only when these prices add up to the
 * line's own checkout total. Underscore-prefixed, so hidden at checkout.
 */
const COMPONENTS_ATTRIBUTE = '_ea_analytics_components';

/**
 * Attribute value for a cart: per logical line, its `_add_to_cart_id`, parent
 * variant, total, and components (sku, name, `_item_type`, qty, paid line cents,
 * list unit cents, product type). null when a logical line's components are not
 * known yet (the caller fetches and retries); '' when there are none.
 * With `strict` (cached components), a line that does not reconcile also returns
 * null — the cache may predate a quantity change — so the caller refetches.
 * @param {any} cart
 * @param {Map<string, any[]>} componentsByKey
 * @param {boolean} [strict]
 * @returns {string|null}
 */
function componentsAttributeValue(cart, componentsByKey, strict = false) {
  const logical = (cart?.items || []).filter(isLogicalCartLine);
  const lines = [];
  for (const it of logical) {
    if (!componentsByKey.has(it.key)) return null;
    const parts = expandLogicalLines([it], componentsByKey);
    if (parts.length === 1 && parts[0] === it) {
      if (strict) return null;
      continue; // did not reconcile: leave whole
    }
    lines.push({
      a: it.properties?._add_to_cart_id || '',
      pv: String(it.variant_id),
      tc: Math.round(paidLineCents(it)),
      c: parts.map((l) => ({
        s: l.sku || '',
        n: l.product_title || l.title || '',
        t: String(l.properties?._item_type || ''),
        q: Number(l.quantity) || 1,
        c: Math.round(paidLineCents(l)),
        u: Math.round(Number(l.original_price ?? l.price ?? 0)),
        pt: l.product_type || '',
      })),
    });
  }
  return lines.length ? JSON.stringify({ v: 1, lines }) : '';
}

/**
 * Keep the cart's COMPONENTS_ATTRIBUTE in step with its logical lines. Runs on load
 * and after every cart change (product_removed reconcile), so it is current when
 * the shopper reaches checkout; writes only when the value changed.
 * @param {any} cart
 */
function syncComponentsAttribute(cart) {
  if (!cart || !Array.isArray(cart.items)) return;
  const current = String(cart.attributes?.[COMPONENTS_ATTRIBUTE] || '');
  const write = (/** @type {string|null} */ value) => {
    if (value == null || value === current) return;
    const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [COMPONENTS_ATTRIBUTE]: value } }),
    }).catch(() => {});
  };
  const cached = componentsAttributeValue(cart, cartComponentsCache, true);
  if (cached != null) {
    write(cached);
    return;
  }
  fetchCartComponents().then((map) => {
    if (map) write(componentsAttributeValue(cart, map));
  });
}

/**
 * The cart line a product-form add just created or grew: same variant as the
 * submitted form (Shopify puts a new line first, so the first match is the
 * newest). null when the form's cart response is unavailable.
 * @param {any} cart
 * @param {Element} formEl
 * @returns {any|null}
 */
function addedFormLine(cart, formEl) {
  const variantId = /** @type {HTMLInputElement|null} */ (formEl.querySelector('input[name="id"]'))?.value;
  if (!variantId || !Array.isArray(cart?.items)) return null;
  return cart.items.find((/** @type {any} */ it) => String(it.variant_id) === String(variantId)) || null;
}

function bundleAddedLines(cart, bundleAdded) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const bundleLines = items.filter((it) => String(it?.properties?._item_type || '') === 'bundle_line_item');
  if (!Array.isArray(bundleAdded)) return bundleLines;
  /** @type {any[]} */
  const out = [];
  bundleAdded.forEach((add) => {
    const qty = Number(add?.quantity) || 0;
    if (qty <= 0) return;
    const line = add?.key
      ? bundleLines.find((it) => it.key === add.key)
      : bundleLines.find((it) => String(it.variant_id) === String(add.variantId));
    if (line) out.push({ ...line, quantity: qty, final_line_price: (paidLineCents(line) / (Number(line.quantity) || 1)) * qty });
  });
  return out;
}

function initCartAdd() {
  document.addEventListener('cart:update', (/** @type {CustomEvent} */ e) => {
    const detail = e.detail;
    if (!detail) return;
    const data = detail.data || {};
    if (data.didError) return;
    // Only fire for genuine adds, identified by the dispatching source. The
    // theme's cart events don't carry a variantId, and qty-change/remove updates
    // (e.g. source 'cart-items-component') must not be counted as adds.
    if (!CART_ADD_SOURCES.includes(data.source)) return;

    // Quantity added comes from the event payload (itemCount); fall back to 1.
    const quantity = Number(data.itemCount) > 0 ? Number(data.itemCount) : 1;

    // Resolve the originating element. The product-form dispatches the event on
    // the <product-form-component> itself, so e.target is the exact form that
    // was submitted — the most reliable source of the clicked product's data.
    const originEl =
      (e.target instanceof HTMLElement ? e.target : null) || (detail.sourceId ? document.getElementById(detail.sourceId) : null);
    const formEl = originEl?.closest?.('product-form-component');

    let products;
    let isByob;
    /** @type {number|undefined} paid total in cents, when known from cart lines */
    let valueCents;

    if (formEl) {
      // Form-based add: PDP / quick-add "Add" / quick-add "Choose" modal /
      // featured product — all carry data-product-* (see buy-buttons.liquid and
      // quick-add.liquid), with window.EAPageData as the PDP fallback.
      const pd = extractProductData(formEl);
      isByob = pd.isByob;
      // Price from the cart line the add landed on (paid, after line discounts),
      // not the catalogue data-product-price; catalogue only when it can't be found.
      const added = addedFormLine(detail.resource, formEl);
      if (added) valueCents = (paidLineCents(added) / (Number(added.quantity) || 1)) * quantity;
      products = [
        {
          sku: pd.sku,
          name: pd.name,
          price: added ? paidUnitPrice(added) : pd.price,
          originalPrice: added ? Number(added.original_price ?? added.price ?? 0) / 100 : pd.price,
          quantity,
          addToCartLocation: pd.addToCartLocation,
          merchCategory: pd.merchCategory,
          variant: cleanVariant(pd.variant),
          occasionSelected: formEl.dataset.productOccasion || window.EAPageData?.productOccasion || '',
          marketplacePartner: pd.marketplacePartner,
          ...arrangementIdField(pd.arrangementId),
        },
      ];
    } else {
      // Document-dispatched add (personalization drawer, BYOB bundle builder):
      // e.target is `document`, so there's no form to read. These dispatch the
      // /cart/add.js response as the event resource — the authoritative list of
      // just-added lines — so read the product data from there. BYOB legitimately
      // adds several products; report them all (companions excluded).
      isByob = data.source === 'ea-bundle-builder-drawer';
      // Opening the builder's cart with nothing new added is not an add.
      if (isByob && Array.isArray(data.bundleAdded) && !data.bundleAdded.length) return;
      // Document-dispatched adds (personalization drawer) carry no originating
      // element, so we can't read the UI surface — derive the location from the
      // page instead. Previously this hardcoded 'plp' for every non-PDP page,
      // which mislabelled homepage / search adds as 'plp' (QA: "addToCartLocation
      // is recording PLP for every cart_add event").
      const addToCartLocation = isByob ? 'byob' : addLocationFromPageType(window.EAPageData?.pageType);
      // Drawer add carrying add-on / upgrade / greeting-card lines: the /cart/add.js
      // response can show an upgrade at $0 (the Cart Transform prices it in the
      // cart), so re-read /cart.js and use those lines (same keys) for prices before
      // deciding which add-ons are paid. Falls back to the response if it fails.
      const responseItems = Array.isArray(detail.resource?.items) ? detail.resource.items : detail.resource?.id ? [detail.resource] : [];
      const hasLogical = responseItems.some(isLogicalCartLine);
      if (!isByob && (hasLogical || responseItems.some((it) => ADDON_ITEM_TYPES.has(String(it?.properties?._item_type || ''))))) {
        // Pre-add quantities, captured now — before any await lets the reconcile
        // overwrite them with the post-add cart.
        const qtyBefore = new Map(cartLineQuantities);
        Promise.all([fetchCart(), hasLogical ? fetchCartComponents() : null]).then(([cart, components]) => {
          const byKey = new Map((cart?.items || []).map((/** @type {any} */ it) => [it.key, it]));
          // Live prices, scaled to the ADDED quantity: the live line's totals cover
          // its whole cart quantity, which can exceed what this add contributed.
          // `_componentQuantity` tells expandLogicalLines what quantity its cached
          // components were priced for, so it scales them the same way.
          const priced = responseItems.map((it) => {
            const live = byKey.get(it.key);
            if (!live) return it;
            const liveQty = Number(live.quantity) || 1;
            // Added units = new total - total before the add. Without a pre-add
            // reading (the line was new, or no baseline yet) the whole line was added.
            const delta = liveQty - (qtyBefore.get(it.key) || 0);
            const addedQty = delta > 0 ? delta : liveQty;
            const ratio = addedQty / liveQty;
            return {
              ...it,
              ...live,
              quantity: addedQty,
              final_line_price: Number(live.final_line_price ?? 0) * ratio,
              line_price: Number(live.line_price ?? 0) * ratio,
              _componentQuantity: liveQty,
            };
          });
          // Logical lines (bundle-ready arrangements) split into their components.
          const expanded = expandLogicalLines(priced, components);
          const lines = productsFromCartAddResponse({ items: expanded }, addToCartLocation);
          if (lines.length) emitCartAdd(lines, false, cartAddValueCents({ items: expanded }));
        });
        return;
      }

      const addedResource = isByob ? { items: bundleAddedLines(detail.resource, data.bundleAdded) } : detail.resource;
      products = productsFromCartAddResponse(addedResource, addToCartLocation);
      if (products.length) valueCents = cartAddValueCents(addedResource);

      // A bundle add with no matching bundle line has nothing to report — the PDP
      // fallback below would describe the page's product, not the bundle.
      if (isByob && !products.length) return;

      // Fallback: response had no usable lines — use EAPageData (PDP context).
      if (!products.length) {
        const pd = extractProductData(null);
        products = [
          {
            sku: pd.sku,
            name: pd.name,
            price: pd.price,
            quantity,
            addToCartLocation,
            merchCategory: pd.merchCategory,
            variant: cleanVariant(pd.variant),
            occasionSelected: window.EAPageData?.productOccasion || '',
            marketplacePartner: pd.marketplacePartner,
            ...arrangementIdField(pd.arrangementId),
          },
        ];
      }
    }

    emitCartAdd(products, isByob, valueCents);
  });
}

/**
 * Unrounded paid total (cents) of the reportable lines in an add response.
 * @param {any} resource
 * @returns {number}
 */
function cartAddValueCents(resource) {
  const items = Array.isArray(resource?.items) ? resource.items : resource?.id ? [resource] : [];
  return items.filter(isReportableProductLine).reduce((sum, it) => sum + paidLineCents(it), 0);
}

/**
 * @param {any[]} products
 * @param {boolean} isByob
 * @param {number} [valueCents] paid total from the cart lines, summed unrounded
 */
function emitCartAdd(products, isByob, valueCents) {
  // GA4 add_to_cart expects an event-level value (currency rides shared
  // context): the lines' paid totals summed unrounded and rounded once; only
  // when no cart line is known, unit price × quantity.
  const value =
    valueCents != null && Number.isFinite(valueCents)
      ? Math.round(valueCents) / 100
      : Number(products.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0).toFixed(2));

  // zipCode / serviceable / deliveryMethod: the saved fulfilment selection at the
  // moment of the add (omitted when the shopper hasn't picked a zip yet).
  pushEvent('cart_add', { products, value, isByob, ...getFulfillmentContext() });
}

/**
 * Map a /cart/add.js response (document-dispatched adds: personalization drawer,
 * BYOB bundle builder) to reportable product lines, excluding auto-added
 * companion lines (gift message, gift wrap, add-ons, etc.). The add response is
 * `{ items: [...] }` for a multi-item add, or a single line object.
 * @param {any} resource
 * @param {string} addToCartLocation
 */
function productsFromCartAddResponse(resource, addToCartLocation) {
  const items = Array.isArray(resource?.items) ? resource.items : resource?.id ? [resource] : [];

  return items.filter(isReportableProductLine).map((it) => ({
    sku: it.sku || '',
    name: it.product_title || it.title || '',
    price: paidUnitPrice(it),
    originalPrice: listUnitPrice(it),
    quantity: it.quantity || 1,
    addToCartLocation,
    ...lineCategoryFields(it, items),
    variant: variantFromLine(it),
    occasionSelected: window.EAPageData?.productOccasion || '',
    marketplacePartner: 'none',
    ...arrangementIdFromLine(it),
  }));
}

/**
 * Walk up from the triggering element to find product metadata.
 * Product forms and product cards embed data-* attributes in the theme.
 * @param {HTMLElement|null} el
 * @returns {{sku:string, name:string, price:number, addToCartLocation:string, merchCategory:string, variant:string, marketplacePartner:string, arrangementId:string, isByob:boolean}}
 */
function extractProductData(el) {
  const root = el || document.body;

  // product-form-component carries data-product-* on itself or a parent. We do
  // NOT fall back to a document-wide querySelector: on the homepage/PLP that
  // grabbed the first product form on the page (a different product), which
  // produced the phantom "wrong product" in cart_add. When there's no form in
  // context we rely on window.EAPageData (PDP) below instead.
  const form = root.closest?.('product-form-component') || root.closest?.('[data-product-id]') || null;

  const isOnPDP =
    document.querySelector('[data-template="product"], [data-page-type="product"]') !== null || window.EAPageData?.pageType === 'pdp';

  // Best-effort add-to-cart surface: byob | carousel | quick-add | pdp | plp
  const isByob = Boolean(root.closest('[data-byob], ea-bundle-builder-drawer'));
  let addToCartLocation = isOnPDP ? 'pdp' : 'plp';
  if (isByob) addToCartLocation = 'byob';
  else if (root.closest('[data-quick-add], quick-add-component, [class*="quick-add"]')) addToCartLocation = 'quick-add';
  else if (root.closest('swipable-group, [class*="carousel"], [class*="swiper"]')) addToCartLocation = 'carousel';

  return {
    sku: form?.dataset.productSku || window.EAPageData?.productSku || '',
    name: form?.dataset.productTitle || window.EAPageData?.productTitle || '',
    price: parseFloat(form?.dataset.productPrice || window.EAPageData?.productPrice || '0') / 100,
    addToCartLocation,
    merchCategory: form?.dataset.productCategory || window.EAPageData?.productCategory || '',
    variant: form?.dataset.selectedVariant || form?.querySelector('input[name="id"]')?.value || '',
    marketplacePartner: form?.dataset.marketplacePartner || window.EAPageData?.marketplacePartner || 'none',
    // Raw (prefixed) value — callers strip it via arrangementIdField(). No PDP
    // fallback when a form IS present: a related-products quick-add on a PDP must
    // not inherit the page product's id.
    arrangementId: form ? arrangementIdFromElement(form) : window.EAPageData?.productArrangementId || '',
    isByob,
  };
}

/**
 * Fetch the authoritative cart as JSON (null on failure).
 * Shared by product_removed (snapshot diff) and coupon_applied (seed known codes).
 * @returns {Promise<any>}
 */
function fetchCart() {
  // Shared with every other cart reader on the page when @theme/cart-state has loaded
  // (it publishes this for classic scripts like this one — see that module). Falls back
  // to its own request so analytics never depends on load order.
  const shared = /** @type {any} */ (window).__eaFetchCartJson;
  if (typeof shared === 'function') return shared().catch(() => null);
  const root = /** @type {any} */ (window).Shopify?.routes?.root || '/';
  return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .catch(() => null);
}

/* ─────────────────────────────────────────────────────────────────────────────
   7b. PRODUCT REMOVED
   The theme's cart:update event fires on add/remove/qty-change but often carries
   a null resource, so we diff the authoritative /cart.js against a snapshot and
   emit product_removed for any line whose quantity decreased. (Adds are handled
   by initCartAdd via the event's variantId, so we only fire on decreases here.)
───────────────────────────────────────────────────────────────────────────── */

// Line-item `_item_type` values that are auto-managed companions of a real
// product (added/removed by the personalization + bundle logic, not chosen by
// the shopper as standalone products). Mirrors CART_ITEM_TYPES in
// assets/cart-contract.js. These must never be reported as product_removed —
// e.g. adding a product auto-adds a free gift-message line, whose churn was
// previously misfiring product_removed.
const COMPANION_ITEM_TYPES = new Set([
  'gift_message',
  'digital_card',
  'greeting_card',
  'add_on',
  'upgrade',
  // The EA gratuity line (EA-3697). Not a product at all — it is money, and a
  // shopper raising or lowering their tip would otherwise fire product_removed
  // and inflate product counts with a line nobody added to a cart.
  'tip',
  // The EA store surcharge line (EA-4351): a fee the cart drawer adds from EA's
  // availability record. Same reasoning as the tip.
  'surcharge',
]);

/**
 * Personalization Drawer add-on line types. These are companions while free, but
 * a PAID add-on / upgrade / greeting card is a product line of its own (EA-4485:
 * confirmed — track them on Product Added, Cart Viewed, Product Removed and
 * Order Completed with category "Add-on" and the parent product's sku). Upgrades
 * are $0 in Shopify until the Cart Transform prices them, so a line only counts
 * once it carries a price.
 */
const ADDON_ITEM_TYPES = new Set(['add_on', 'upgrade', 'greeting_card']);
const ADDON_MERCH_CATEGORY = 'Add-on';
const CYO_CHILD_TYPES = new Set(['cyo', 'cyo_gift_wrap']);

/**
 * @param {any} item
 * @returns {boolean}
 */
function isPaidAddonLine(item) {
  const type = String(item?.properties?._item_type || '');
  return ADDON_ITEM_TYPES.has(type) && paidUnitPrice(item) > 0;
}

/**
 * Category and parent sku for a reported line. A paid drawer add-on reports
 * merchCategory "Add-on" and parentSku = the sku of the line it was added with
 * (same `_add_to_cart_id`, `_item_type: parent`, else any other reportable,
 * non-add-on line in that group). Other lines keep their product type.
 * @param {any} it
 * @param {any[]} lines - lines to look the parent up in (the add response / cart)
 * @returns {{merchCategory: string, parentSku?: string}}
 */
function lineCategoryFields(it, lines) {
  if (!isPaidAddonLine(it)) return { merchCategory: it?.product_type || '' };
  const group = it?.properties?._add_to_cart_id;
  const siblings = group ? (lines || []).filter((l) => l !== it && l?.properties?._add_to_cart_id === group) : [];
  const parent =
    siblings.find((l) => String(l?.properties?._item_type || '') === 'parent') ||
    siblings.find((l) => !COMPANION_ITEM_TYPES.has(String(l?.properties?._item_type || '')));
  return parent?.sku ? { merchCategory: ADDON_MERCH_CATEGORY, parentSku: String(parent.sku) } : { merchCategory: ADDON_MERCH_CATEGORY };
}

/**
 * A cart line is reportable as a "product" unless it is an auto-managed
 * companion (gift message, digital card, tip, surcharge) or a FREE add-on.
 * Paid add-ons / upgrades / greeting cards are reported (EA-4485).
 * @param {any} item
 * @returns {boolean}
 */
function isReportableProductLine(item) {
  const props = item?.properties || {};
  const type = String(props._item_type || '');
  // `_is_bundle_addon` is NOT excluded: it marks a product the shopper picked in the
  // bundle builder's Step 2, a real product line (Anatta defects 2026-10-27).
  if (ADDON_ITEM_TYPES.has(type)) return paidUnitPrice(item) > 0;
  if (COMPANION_ITEM_TYPES.has(type)) return false;
  // Create-Your-Own children (flavours, gift wrap) are the box's contents, priced
  // into the box: a $0 one is not a product (same rule as the gift message). A
  // priced one still is.
  if (CYO_CHILD_TYPES.has(type)) return paidUnitPrice(item) > 0;
  return true;
}

function initProductRemoved() {
  /** @type {Record<string, {quantity:number, item:any}>|null} */
  let snapshot = null;

  function buildSnapshot(/** @type {any} */ cart) {
    /** @type {Record<string, {quantity:number, item:any}>} */
    const map = {};
    (cart.items || []).forEach((/** @type {any} */ it) => {
      // Only track genuine product lines, so companion churn (e.g. the free
      // gift-message line) can't register as a removal.
      if (!isReportableProductLine(it)) return;
      map[it.key] = { quantity: it.quantity, item: it };
    });
    return map;
  }

  // Logical lines' components are needed to report a removal after the line is gone,
  // and to hand the checkout pixel its component prices (cart attribute).
  function primeComponents(/** @type {any} */ cart) {
    syncComponentsAttribute(cart);
  }

  // Establish the baseline.
  let pending = fetchCart().then((cart) => {
    if (cart) snapshot = buildSnapshot(cart);
    rememberCartQuantities(cart);
    primeComponents(cart);
  });

  // Debounce so a single user action that emits several cart:update events in
  // quick succession is reconciled once against the last stable snapshot.
  //
  // Add handling is surgical rather than a blanket time window: an add naturally
  // increases an existing line's quantity or introduces a new line, so on an add
  // burst we skip cart_quantity_changed for increases (cart_add already covers
  // them) but STILL emit product_removed for any unrelated line that was fully
  // removed. Companion lines (gift message, wrap, etc.) are excluded from the
  // snapshot entirely, so their churn never surfaces here regardless.
  let debounceTimer = null;
  let burstHadAdd = false;
  let lastProgressValue = null;

  function scheduleReconcile(/** @type {CustomEvent} */ e) {
    if (CART_ADD_SOURCES.includes(e?.detail?.data?.source)) burstHadAdd = true;

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const addBurst = burstHadAdd;
      burstHadAdd = false;
      pending = pending
        .then(fetchCart)
        .then((cart) => {
          if (!cart) return;
          const next = buildSnapshot(cart);
          rememberCartQuantities(cart);
          primeComponents(cart);

          const snap = snapshot;
          if (snap) {
            const cartLocation = document.querySelector('ea-cart-drawer')?.querySelector('dialog[open]') ? 'drawer' : 'page';

            Object.keys(snap).forEach((key) => {
              const before = snap[key];
              if (!before) return;
              const after = next[key];
              // product_removed fires ONLY when a line is fully removed — gone
              // from the cart, or its quantity dropped to 0. A partial quantity
              // decrease (e.g. 3 → 2) is NOT a removal and emits nothing here.
              const isRemoved = !after || after.quantity === 0;
              if (isRemoved) {
                // A removal is never caused by an add, so always report it —
                // even during an add burst (an unrelated line was removed).
                // A logical line reports its components (arrangement + paid add-ons),
                // like cart_add did; any other line reports itself.
                const removedLines = expandLogicalLines([{ ...before.item, quantity: before.quantity }], cartComponentsCache).filter(
                  isReportableProductLine,
                );
                const context = [...Object.values(snap).map((entry) => entry.item), ...removedLines];
                pushEvent('product_removed', {
                  products: removedLines.map((it) => ({
                    sku: it.sku || '',
                    name: it.product_title || it.title || '',
                    price: paidUnitPrice(it),
                    originalPrice: listUnitPrice(it),
                    quantity: it.quantity,
                    variant: variantFromLine(it),
                    ...lineCategoryFields(it, context),
                    ...arrangementIdFromLine(it),
                  })),
                  // GA4 remove_from_cart expects event-level value; currency
                  // rides shared context.
                  value: Math.round(removedLines.reduce((sum, it) => sum + paidLineCents(it), 0)) / 100,
                });
              } else if (after.quantity > before.quantity) {
                // Increase. On an add burst this IS the add (cart_add covers it);
                // only a genuine stepper increase should fire cart_quantity_changed.
                if (addBurst) return;
                const it = after.item;
                pushEvent('cart_quantity_changed', {
                  sku: it.sku || '',
                  ...arrangementIdFromLine(it),
                  previousQuantity: before.quantity,
                  newQuantity: after.quantity,
                  location: cartLocation,
                });
              }
            });
          }

          // Free Delivery Threshold Progress — only when the cart value actually
          // changed since the last emission, while still below the threshold.
          const threshold = Number(window.EAPageData?.freeDeliveryThreshold) || 0;
          if (threshold > 0 && cart.total_price != null) {
            // MERCHANDISE value, not total_price. The gratuity line (EA-3697) and the
            // store surcharge line (EA-4351) are cart lines and therefore inside total_price, so reporting total_price here would
            // tell analytics a shopper was closer to the free-delivery threshold than
            // the drawer actually treats them — the event would disagree with the bar
            // the shopper is looking at. Same rule as merchandiseTotalCents() in
            // assets/cart-contract.js, reimplemented because this file is a plain
            // script with no imports; pinned to it by tests/gratuity-contract.test.mjs.
            const gratuityCents = cartFeeCents(cart);
            const merchandiseCents = Math.max(0, cart.total_price - gratuityCents);
            const cartValue = Number((merchandiseCents / 100).toFixed(2));
            if (cartValue > 0 && cartValue < threshold && cartValue !== lastProgressValue) {
              lastProgressValue = cartValue;
              pushEvent('free_delivery_progress', {
                cartValue,
                thresholdAmount: threshold,
                distanceToThreshold: Number((threshold - cartValue).toFixed(2)),
              });
            }
          }

          snapshot = next;
        })
        .catch(() => {});
    }, 300);
  }

  document.addEventListener('cart:update', scheduleReconcile);
}

/* ─────────────────────────────────────────────────────────────────────────────
   7c. COUPON APPLIED
   Success: theme's discount:update carries cart.discount_codes[] with applicable
   flags — we fire for newly-added applicable codes (ignoring removals).
   Failure: cart-discount.js dispatches ea:coupon-attempt for invalid codes,
   which otherwise return early with no event (see cart-discount.js).
───────────────────────────────────────────────────────────────────────────── */

/**
 * Total discount on an AJAX cart, in major units. `total_discount` alone reported
 * 0 for TEST100 in UAT (Segment "Coupon Applied"): it can lag cart-level
 * (order) discount applications, so take the largest of the three views the
 * cart JSON gives — total_discount, the sum of discount applications + line
 * discounts, and the original-minus-final price gap.
 * @param {any} cart
 * @returns {number|undefined}
 */
function cartDiscountAmount(cart) {
  if (!cart) return undefined;
  const cents = (/** @type {unknown} */ v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const fromTotal = cents(cart.total_discount);
  const cartLevel = (Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []).reduce(
    (/** @type {number} */ sum, /** @type {any} */ a) => sum + cents(a.total_allocated_amount),
    0,
  );
  const lineLevel = (Array.isArray(cart.items) ? cart.items : []).reduce(
    (/** @type {number} */ sum, /** @type {any} */ it) => sum + cents(it.line_level_total_discount),
    0,
  );
  const fromGap = Math.max(0, cents(cart.original_total_price) - cents(cart.total_price));
  return Math.max(fromTotal, cartLevel + lineLevel, fromGap) / 100;
}

function initCouponApplied() {
  /** @type {string[]} */
  let knownCodes = [];

  // Seed from the current cart so codes already applied before this script loaded
  // aren't re-reported as newly applied on the first discount:update.
  const seeded = fetchCart().then((cart) => {
    if (cart && Array.isArray(cart.discount_codes)) {
      knownCodes = cart.discount_codes
        .filter((/** @type {{applicable?:boolean}} */ c) => c.applicable !== false)
        .map((/** @type {{code:string}} */ c) => c.code);
    }
  });

  document.addEventListener('discount:update', (/** @type {CustomEvent} */ e) => {
    const cart = e.detail?.resource;
    const codes = (cart && cart.discount_codes) || [];
    const applicable = codes
      .filter((/** @type {{applicable:boolean}} */ c) => c.applicable)
      .map((/** @type {{code:string}} */ c) => c.code);

    // Defer the diff until the initial seed has completed, so a discount:update
    // arriving before seeding doesn't report already-applied codes as new.
    // Callbacks on `seeded` run in registration order, keeping updates sequential.
    seeded.then(() => {
      applicable
        .filter((/** @type {string} */ code) => !knownCodes.includes(code))
        .forEach((/** @type {string} */ code) => {
          pushEvent('coupon_applied', {
            couponCode: code,
            status: 'applied',
            discountAmount: cartDiscountAmount(cart),
            cartValue: cart && cart.total_price != null ? cart.total_price / 100 : undefined,
          });
        });

      knownCodes = applicable;
    });
  });

  // Failed attempts (invalid / expired / min-not-met) surfaced by cart-discount.js
  document.addEventListener('ea:coupon-attempt', (/** @type {CustomEvent} */ e) => {
    const d = e.detail || {};
    if (!d.code) return;
    pushEvent('coupon_applied', {
      couponCode: d.code,
      status: d.status || 'invalid',
      discountAmount: d.discountAmount,
      cartValue: d.cartValue,
    });
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   8. PRODUCT VIEW (PDP)
   Fires once on PDP load using data injected by theme.liquid.
───────────────────────────────────────────────────────────────────────────── */

function initProductView() {
  const d = window.EAPageData;
  if (!d || d.pageType !== 'pdp') {
    // TEMP(UAT DEBUG): explain why product_view was skipped. REMOVE after UAT.
    console.log('%c[EA DataLayer] product_view SKIPPED', 'color:#ffa726;font-weight:bold', {
      hasEAPageData: !!d,
      pageType: d?.pageType,
      expected: 'pdp',
    });
    return;
  }

  pushEvent('product_view', {
    product: {
      sku: d.productSku || '',
      // productId MUST be the numeric Shopify product id: EA_DW's
      // product_viewed.product_id is BIGINT, so the old "US_<sku>_<title>" string
      // landed as NULL on every row (UAT Defects row 50). The formatted id moves
      // to its own field; GTM already builds the US_ sku for the sku property.
      productId: Number(d.productId) || null,
      productIdFormatted: d.productIdFormatted || '',
      name: d.productTitle || '',
      price: d.productPrice ? d.productPrice / 100 : 0,
      regularPrice: d.productCompareAtPrice ? d.productCompareAtPrice / 100 : null,
      // Single-variant products carry Shopify's "Default Title" placeholder — not a
      // variant. Cleaned exactly like cart_add (cleanVariant), so both send '' for the
      // same product (Anatta defects 2026-10-27, "Product Viewed variant is Default Title").
      variant: cleanVariant(d.productVariantTitle),
      merchCategory: d.productCategory || '',
      occasion: d.productOccasion || '',
      marketplacePartner: d.marketplacePartner || 'none',
      ...arrangementIdField(d.productArrangementId),
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   8b. VIEW ITEM LIST (PLP / collection)  +  8d. SELECT ITEM (product-in-list click)
   view_item_list fires once on collection load; select_item fires on a product
   card click in a list. (GA4 - Additional Suggested Events, CRO Must-Have.)
   Product cards only expose data-product-id in the DOM, so items[] carries
   item_id + index (position); item_list_name is the required field.
───────────────────────────────────────────────────────────────────────────── */

// A product-card grid marks a "list" context (collection, search, carousels).
const PRODUCT_LIST_ITEM = '.product-card[data-product-id], [data-product-grid] [data-product-id]';

function initCollectionView() {
  const d = window.EAPageData;
  if (!d || d.pageType !== 'plp') return;

  const cards = [...document.querySelectorAll(PRODUCT_LIST_ITEM)];
  const items = cards.map((c, i) => ({
    item_id: c.getAttribute('data-product-id') || '',
    ...arrangementIdField(arrangementIdFromElement(c)),
    index: i + 1,
  }));

  pushEvent('view_item_list', {
    ecommerce: {
      item_list_id: d.collectionId || '',
      item_list_name: d.collectionTitle || '',
      ...(items.length ? { items } : {}),
    },
  });
}

function initSelectItem() {
  document.addEventListener('click', (e) => {
    const target = /** @type {HTMLElement} */ (e.target);

    // Ignore clicks on interactive card controls — quick-add / add-to-cart
    // buttons, swatches, and other form controls are add/config actions, not a
    // product selection. Only genuine card navigation/selection fires select_item.
    if (
      target.closest(
        'button, [type="submit"], [role="button"], input, select, label, ' +
          'quick-add-component, add-to-cart-component, [data-quick-add], [class*="quick-add"]',
      )
    )
      return;

    const card = target.closest(PRODUCT_LIST_ITEM);
    if (!card) return;

    // The list the card sits in (collection grid, search, PDP related, carousel).
    const listRoot =
      card.closest(
        '[data-product-grid], .product-grid, .collection, search-results, predictive-search-component, swipable-group, [class*="carousel"]',
      ) || null;
    const siblings = [...(listRoot || document).querySelectorAll(PRODUCT_LIST_ITEM)];
    const index = siblings.indexOf(card) + 1;

    // item_list_name: collection title on a PLP, else the containing list's own
    // label/heading (PDP related-products, homepage carousels), else search.
    const listName =
      window.EAPageData?.collectionTitle ||
      listRoot?.getAttribute('data-list-name') ||
      listRoot?.getAttribute('aria-label') ||
      listRoot?.querySelector('h2,h3,[class*="heading"],[class*="title"]')?.textContent?.trim() ||
      (window.EAPageData?.pageType === 'search' ? 'search-results' : '');

    pushEvent('select_item', {
      ecommerce: {
        item_list_name: listName,
        items: [
          {
            item_id: card.getAttribute('data-product-id') || '',
            item_name: card.querySelector('.product-card__title, [class*="title"]')?.textContent?.trim() || '',
            ...arrangementIdField(arrangementIdFromElement(card)),
            index: index > 0 ? index : undefined,
          },
        ],
      },
    });
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   9. SITE ERROR
   errorType enum: 404 | null-search | area-not-serviced | order-track-failed |
   form-validation | form-submit-failed | payment-error.
   - 404 fires here on the 404 template.
   - null-search fires inline from initSearch(); area-not-serviced from
     initZipEntered() (via ea:zip-serviceability).
   - form-validation (client-side field validation) and form-submit-failed
     (submission/API request failure) are dispatched by the form surfaces
     (contact form, free-gift signup) via the generic listener below.
   - The remaining types (order-track-failed, payment-error) have no source in
     the theme yet. Feature code should dispatch them through the generic
     listener below rather than pushing site_error directly:

       document.dispatchEvent(new CustomEvent('ea:site-error', {
         bubbles: true,
         detail: {
           errorType,        // one of the enum values above
           errorMessage,     // human-readable
           pageType,         // optional
           zipCode,          // optional (area-not-serviced)
           orderNumber,      // optional (order-track-failed)
         },
       }));
───────────────────────────────────────────────────────────────────────────── */

function initSiteError() {
  if (window.EAPageData?.pageType === '404') {
    pushEvent('site_error', {
      errorType: '404',
      errorMessage: `Page not found: ${window.location.pathname}`,
      pageType: '404',
    });
  }

  // Generic entry point for feature-dispatched errors (order tracking, form /
  // payment validation, etc.). Only the provided fields are forwarded.
  document.addEventListener('ea:site-error', (/** @type {CustomEvent} */ e) => {
    const d = e.detail || {};
    if (!d.errorType) return;
    pushEvent('site_error', {
      errorType: d.errorType,
      errorMessage: d.errorMessage || '',
      ...(d.pageType ? { pageType: d.pageType } : { pageType: window.EAPageData?.pageType || '' }),
      ...(d.zipCode ? { zipCode: d.zipCode } : {}),
      ...(d.orderNumber ? { orderNumber: d.orderNumber } : {}),
    });
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   10–12. FORM EVENTS (view / start / complete)
   Covers newsletter signup in footer, contact form, account forms.
   Payment-page opt-in rides Order Completed (handled in pixel sandbox).
───────────────────────────────────────────────────────────────────────────── */

// Forms we explicitly track. The Zendesk contact form and the Cordial
// newsletter form are custom-element forms with no `action` attribute (they
// submit via fetch), so attribute-URL guessing can't see or name them — they
// must be tagged with data-gtm-form-name in Liquid. This selector is the single
// source of truth for which forms are observed and named.
const TRACKED_FORM_SELECTOR = [
  'form[data-gtm-form-name]',
  '.contact-form-custom__form',
  '.cordial-email-signup__form',
  'form[action*="/contact"]',
  'form[action*="customer"]',
  'form[data-type="newsletter"]',
  'footer form',
].join(', ');

function getFormName(form) {
  // An explicit data-gtm-form-name always wins (set on the JS-submitted forms).
  const dataName = form.dataset?.gtmFormName;
  if (dataName) return dataName;

  const action = form.action || '';
  // Contact detection must run first: a contact form placed in the footer
  // would otherwise be misclassified as 'newsletter-signup' by the footer check.
  if (form.closest('.contact-form-custom__form') || action.includes('/contact') || form.id?.includes('contact')) return 'contact';
  // The Shopify locale/currency selector ({% form 'localization' %}) can sit in
  // the footer; exclude it so a locale change isn't tracked as a newsletter signup.
  if (action.includes('/localization')) return '';
  if (form.closest('.cordial-email-signup__form') || form.closest('footer') || form.dataset?.type === 'newsletter')
    return 'newsletter-signup';
  if (action.includes('/account') || form.id?.includes('customer')) return 'account-registration';
  return '';
}

function getFormLocation(form) {
  const dataLoc = form?.dataset?.gtmFormLocation;
  if (dataLoc) return dataLoc;
  if (form.closest('footer')) return 'footer';
  if (form.closest('dialog, .global-drawer, [class*="modal"]')) return 'modal';
  return 'page';
}

function initFormEvents() {
  if (!('IntersectionObserver' in window)) return;

  // ── Form View ─────────────────────────────────────────────────────────────
  const viewObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const form = /** @type {HTMLElement} */ (entry.target);
        if (form.dataset.gtmFormViewed) return;
        form.dataset.gtmFormViewed = 'true';

        const name = getFormName(/** @type {HTMLFormElement} */ (form));
        if (!name) return;

        pushEvent('form_view', {
          formName: name,
          formLocation: getFormLocation(/** @type {HTMLFormElement} */ (form)),
        });

        viewObserver.unobserve(form);
      });
    },
    { threshold: 0.5 },
  );

  const observeForms = (root = document) => root.querySelectorAll(TRACKED_FORM_SELECTOR).forEach((f) => viewObserver.observe(f));
  observeForms();

  // Custom-element forms (Zendesk / Cordial) can hydrate after this script runs,
  // so also pick up any tracked forms added to the DOM later.
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches?.(TRACKED_FORM_SELECTOR)) viewObserver.observe(node);
          node.querySelectorAll(TRACKED_FORM_SELECTOR).forEach((f) => viewObserver.observe(f));
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  // ── Form Start (first field interaction) ──────────────────────────────────
  // This is exactly the "focus entered the form" trigger — the first time focus
  // lands on any field of a named form (e.g. the footer newsletter input), we
  // fire form_start once.
  document.addEventListener(
    'focusin',
    (e) => {
      const input = /** @type {HTMLElement} */ (e.target).closest('input:not([type="hidden"]), textarea, select');
      if (!input) return;
      const form = input.closest('form');
      if (!form || form.dataset.gtmFormStarted) return;

      const name = getFormName(form);
      if (!name) return;

      form.dataset.gtmFormStarted = 'true';
      pushEvent('form_start', {
        formName: name,
        formLocation: getFormLocation(form),
      });
    },
    { passive: true },
  );

  // ── Form Complete ──────────────────────────────────────────────────────────
  // Native-submit forms (standard Shopify contact/customer forms) complete on
  // the submit event. JS-submitted forms (Zendesk, Cordial) opt out with
  // data-gtm-complete-on="event" and instead fire form_complete only on genuine
  // success via the ea:form-submitted event below — so a failed validation or
  // API error no longer counts as a completion.
  // Optional opt-in metadata (Form Submitted spec): opt-in source. Read from
  // form data-* or the ea:form-submitted detail; only included when present.
  // EA-4411: no signup value (raw or hashed email) is ever forwarded.
  const formExtras = (form, detail = {}) => {
    let optInProperty = detail.optInProperty || form?.dataset?.gtmOptInProperty || '';

    // Derive optInProperty from accepts_marketing when not explicitly set.
    // Shopify newsletter forms include a contact[accepts_marketing] field;
    // its presence (hidden=1 or checkbox=checked) indicates email opt-in.
    if (!optInProperty && form) {
      const mktField = /** @type {HTMLInputElement|null} */ (form.querySelector('[name="contact[accepts_marketing]"]'));
      if (mktField) {
        const optedIn = mktField.type === 'checkbox' ? mktField.checked : mktField.value === '1';
        if (optedIn) optInProperty = 'email';
      }
    }

    return {
      ...(optInProperty ? { optInProperty } : {}),
    };
  };

  // EA-4411: newsletter / Cordial completion → Segment identify only (no traits,
  // no email). The signup event itself is GTM's Form Submitted, from the
  // form_complete pushes below — no direct 'Email Signed Up' track (it duplicated it).
  const identifyEmailSignup = (name) => {
    if (name !== 'newsletter-signup') return;
    segmentIdentify();
  };

  document.addEventListener('submit', (e) => {
    const form = /** @type {HTMLFormElement} */ (e.target.closest('form'));
    if (!form) return;
    if (form.dataset?.gtmCompleteOn === 'event') return;
    const name = getFormName(form);
    if (!name || name === 'search') return; // search handled separately

    const formLocation = getFormLocation(form);
    const extras = formExtras(form);
    pushEvent('form_complete', {
      formName: name,
      formLocation,
      ...extras,
    });
    identifyEmailSignup(name);
  });

  // Success signal dispatched by JS-submitted forms after the request succeeds.
  document.addEventListener('ea:form-submitted', (/** @type {CustomEvent} */ e) => {
    const d = e.detail || {};
    const form = /** @type {HTMLFormElement|null} */ (e.target?.closest?.('form')) || null;
    const name = d.formName || (form && getFormName(form));
    if (!name) return;

    const formLocation = d.formLocation || (form ? getFormLocation(form) : 'page');
    const extras = formExtras(form, d);
    pushEvent('form_complete', {
      formName: name,
      formLocation,
      ...extras,
    });
    identifyEmailSignup(name);
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   13. ZIP CODE ENTERED  (+ site_error: area-not-serviced)
   Fires on every zip / serviceability submission across:
   homepage availability widget, PDP delivery selector, store-selector modal,
   BYOB bundle drawer, catering drawer.

   ┌─ INTEGRATION CONTRACT — READ BEFORE WIRING THE SERVICEABILITY API ─────────┐
   │ STATUS (2026-07): The zip INPUT UIs exist (personalization drawer, BYOB    │
   │ drawer, catering drawer) but NO serviceability API is connected yet, so    │
   │ these two tags cannot fire. The listeners below are already in place and   │
   │ waiting — do NOT change them. When you connect the store-locator /         │
   │ serviceability API, the ONLY thing left to do is dispatch this event after │
   │ the API responds (success OR failure):                                     │
   │                                                                            │
   │   document.dispatchEvent(new CustomEvent('ea:zip-serviceability', {        │
   │     bubbles: true,                                                         │
   │     detail: {                                                              │
   │       zipCode,                       // string the shopper entered         │
   │       serviceable,                   // boolean — API says a store serves  │
   │       entrySource,                   // 'pdp' | 'byob' | 'catering' |      │
   │                                      //   'header' | 'homepage' | 'cart'    │
   │     },                                                                     │
   │   }));                                                                     │
   │                                                                            │
   │ That single dispatch fires BOTH zip_entered (always) and, when             │
   │ serviceable === false, site_error{errorType:'area-not-serviced'}. Do not   │
   │ push those events directly from feature code — dispatch the event and let  │
   │ this module own the payload shape.                                         │
   │                                                                            │
   │ Known integration points to update (search "ea:zip-serviceability TODO"):  │
   │   • assets/catering-event.js  → validateZip() (has a mock fetchStoresByZip)│
   │   • assets/personalization-drawer.js / sections/personalization-drawer.liquid (ref="zipcodeInput") │
   │   • assets/ea-bundle-builder-drawer.js / sections/ea-bundle-builder-drawer.liquid (.bb-footer__zip-input) │
   └────────────────────────────────────────────────────────────────────────────┘

   The generic form-submit listener below also emits a preliminary zip_entered
   (serviceable: null) for any <form> containing a zip input, so a zip typed
   into a real submitted form is captured even before the API is wired.
───────────────────────────────────────────────────────────────────────────── */

function getZipEntrySource(form) {
  if (form.closest('header-component')) return 'header';
  if (form.closest('[id*="store"], .store-selector, [class*="store-modal"]')) return 'store-modal';
  if (form.closest('product-form-component, [data-product-id], .product-information')) return 'pdp';
  if (window.EAPageData?.pageType === 'homepage') return 'homepage';
  return 'page';
}

// Zip inputs a shopper types into: the header store-availability dialog, the
// personalization drawer's embedded panel and the PDP availability block all
// render `.store-availability-dialog__zip-input`; the rest catch plain forms.
const ZIP_INPUT_SELECTOR = [
  '.store-availability-dialog__zip-input',
  'input[name*="zip" i]',
  'input[id*="zip" i]',
  'input[name*="postal" i]',
  'input[autocomplete="postal-code"]',
  'input[placeholder*="zip" i]',
  'input[placeholder*="postal" i]',
].join(', ');

// How long after the shopper last typed a zip a serviceability answer still
// counts as the answer to THAT entry (store-location + availability can take a
// few seconds on a slow connection).
const ZIP_INTENT_WINDOW_MS = 20000;

function initZipEntered() {
  // Dedup area-not-serviced site_error: the theme sometimes dispatches
  // ea:zip-serviceability twice for the same zip (once from the API response,
  // once from a UI reaction). Suppress exact duplicates within 2 seconds.
  const recentAreaErrors = /** @type {Map<string, number>} */ (new Map());

  // ── User-intent gate (UAT Defects row 58) ────────────────────────────────
  // store-availability.js and pdp-availability.js dispatch ea:zip-serviceability
  // from their store lookup, and that lookup ALSO runs without the shopper doing
  // anything: the PDP restores the saved zip on load, and the header component
  // re-checks the saved zip on every cart change (Remove, qty → 0, Clear Cart).
  // Each of those produced a zip_entered (and event38 in Adobe) for a zip nobody
  // entered. zip_entered now fires only when the lookup answers a zip the shopper
  // actually typed: a trusted keystroke/paste in a zip input arms the gate, the
  // first answer consumes it.
  /** @type {{at:number, input:HTMLInputElement}|null} */
  let zipIntent = null;
  const normZip = (/** @type {unknown} */ v) =>
    String(v || '')
      .replace(/\s+/g, '')
      .toUpperCase();
  const armIntent = (/** @type {Event} */ e) => {
    if (!e.isTrusted) return; // programmatic restores (input.value = saved) never count
    const el = /** @type {HTMLElement|null} */ (e.target instanceof HTMLElement ? e.target : null);
    if (!el?.matches?.(ZIP_INPUT_SELECTOR)) return;
    zipIntent = { at: Date.now(), input: /** @type {HTMLInputElement} */ (el) };
  };
  document.addEventListener('input', armIntent, true);
  document.addEventListener('paste', armIntent, true);

  /** @type {{zip:string, at:number}|null} */
  let lastReported = null;

  // Intercept form submits that contain a zip/postal input
  document.addEventListener('submit', (e) => {
    const form = /** @type {HTMLFormElement} */ (e.target.closest('form'));
    if (!form) return;

    const zipInput = /** @type {HTMLInputElement|null} */ (form.querySelector(ZIP_INPUT_SELECTOR));
    if (!zipInput) return;

    const zip = zipInput.value?.trim();
    if (!zip || !/^\d{5}(-\d{4})?$|^[A-Za-z]\d[A-Za-z]/.test(zip)) return;

    // Same user-intent gate as the serviceability listener: only a zip the shopper
    // typed into THIS form, recently, and still the value being submitted. A
    // prefilled / programmatically filled form submits nothing. The intent is not
    // consumed here, so a serviceability answer for the entry can still report it.
    if (
      !zipIntent ||
      Date.now() - zipIntent.at > ZIP_INTENT_WINDOW_MS ||
      !form.contains(zipIntent.input) ||
      normZip(zipIntent.input.value) !== normZip(zip)
    )
      return;

    pushEvent('zip_entered', {
      zipCode: zip,
      serviceable: null, // updated below when response returns
      entrySource: getZipEntrySource(form),
    });
  });

  // Listen for serviceability response — store-availability.js / pdp-availability.js
  // dispatch this after the store lookup answers.
  document.addEventListener('ea:zip-serviceability', (/** @type {CustomEvent} */ e) => {
    const { zipCode, serviceable, entrySource, deliveryMethod, storeCount, nearestStoreDistance } = e.detail || {};
    if (!zipCode) return;

    const now = Date.now();
    // Not typed by the shopper (page-load restore, cart-change re-check): skip.
    if (!zipIntent || now - zipIntent.at > ZIP_INTENT_WINDOW_MS) return;
    // ...and it must be the answer for the zip that was typed, not a background
    // re-check of the saved zip that happened to land while the shopper typed.
    if (normZip(zipIntent.input.value) !== normZip(zipCode)) return;
    zipIntent = null;
    // Same zip answered twice in a row (two components share the lookup): once.
    if (lastReported && lastReported.zip === zipCode && now - lastReported.at < 2000) return;
    lastReported = { zip: zipCode, at: now };

    pushEvent('zip_entered', {
      zipCode,
      serviceable: Boolean(serviceable),
      entrySource: entrySource || 'page',
      // Optional serviceability detail — forwarded when the API provides it.
      ...(deliveryMethod ? { deliveryMethod } : {}),
      ...(storeCount != null ? { storeCount } : {}),
      ...(nearestStoreDistance != null ? { nearestStoreDistance } : {}),
    });

    if (!serviceable) {
      const errKey = zipCode;
      if (!recentAreaErrors.has(errKey) || now - /** @type {number} */ (recentAreaErrors.get(errKey)) > 2000) {
        recentAreaErrors.set(errKey, now);
        pushEvent('site_error', {
          errorType: 'area-not-serviced',
          errorMessage: `No stores service ZIP ${zipCode}`,
          zipCode,
          pageType: window.EAPageData?.pageType || '',
        });
      }
    }
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   14. LOYALTY ENROLLED + REWARD REDEEMED  (Edible Rewards / Annex Cloud)
   Neither the loyalty program nor reward redemption is implemented yet, so
   there is no source to fire from today. The listeners below are the contract:
   when Annex Cloud (or the checkout/cart reward flow) is wired, dispatch these
   events and this module owns the dataLayer shape for all three destinations.

     // On successful Edible Rewards sign-up:
     document.dispatchEvent(new CustomEvent('ea:loyalty-enrolled', {
       bubbles: true,
       detail: {
         loyaltyId,             // required — Annex Cloud member id (hashed)
         loyaltyTier,           // optional — tier at enrollment
         enrollLocation,        // optional — footer|account|checkout|pdp
       },
     }));

     // When a loyalty reward / points are applied to an order:
     document.dispatchEvent(new CustomEvent('ea:reward-redeemed', {
       bubbles: true,
       detail: {
         rewardId,              // required
         rewardType,            // optional — points|reward|birthday|tier-perk
         rewardValue,           // optional — discount/credit value
         pointsRedeemed,        // optional
         loyaltyTier,           // optional
       },
     }));
───────────────────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────────────────
   13b. REFERRAL STORE CAPTURED (EA-4288)
   Once, on the page where a franchise referral link (`?store=123`) sets — or, last
   touch, replaces — the cart's referral. Not on later pages, and not when the
   link names the store the cart already carries. referral-attribution.js usually runs
   before this file, so its flag is read first; the event covers the other order.
───────────────────────────────────────────────────────────────────────────── */

function initReferralStoreCaptured() {
  let emitted = false;
  const emit = (detail) => {
    if (emitted || !detail || !detail.referral_store) return;
    emitted = true;
    pushEvent('referral_store_captured', {
      referral_store: String(detail.referral_store),
      attribution_model: detail.attribution_model || '',
    });
  };
  document.addEventListener('ea:referral-store-captured', (/** @type {CustomEvent} */ e) => emit(e.detail));
  const state = window.EAReferralAttribution;
  if (state && state.capturedOnThisPage) {
    emit({ referral_store: state.storeNumber, attribution_model: state.model });
  }
}

function initLoyaltyRewards() {
  document.addEventListener('ea:loyalty-enrolled', (/** @type {CustomEvent} */ e) => {
    const d = e.detail || {};
    if (!d.loyaltyId) return;
    pushEvent('loyalty_enrolled', {
      loyaltyId: d.loyaltyId,
      ...(d.loyaltyTier ? { loyaltyTier: d.loyaltyTier } : {}),
      ...(d.enrollLocation ? { enrollLocation: d.enrollLocation } : {}),
    });
  });

  document.addEventListener('ea:reward-redeemed', (/** @type {CustomEvent} */ e) => {
    const d = e.detail || {};
    if (!d.rewardId) return;
    pushEvent('reward_redeemed', {
      rewardId: d.rewardId,
      ...(d.rewardType ? { rewardType: d.rewardType } : {}),
      ...(d.rewardValue != null ? { rewardValue: d.rewardValue } : {}),
      ...(d.pointsRedeemed != null ? { pointsRedeemed: d.pointsRedeemed } : {}),
      ...(d.loyaltyTier ? { loyaltyTier: d.loyaltyTier } : {}),
    });
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   15. CART VIEWED  (GA4 view_cart)
   Fires when the cart drawer opens or the cart page loads. Cart drawer opens
   never trigger a page load, so without this the cart→checkout funnel breaks.
───────────────────────────────────────────────────────────────────────────── */

function emitCartViewed(source) {
  fetchCart()
    .then((cart) =>
      cart && Array.isArray(cart.items) && cart.items.some(isLogicalCartLine)
        ? fetchCartComponents().then((components) => ({ cart, lines: expandLogicalLines(cart.items, components) }))
        : { cart, lines: cart?.items },
    )
    .then(({ cart, lines }) => {
      if (!cart || !Array.isArray(lines)) return;
      const items = lines.filter(isReportableProductLine).map((it) => ({
        sku: it.sku || '',
        name: it.product_title || it.title || '',
        price: paidUnitPrice(it),
        originalPrice: listUnitPrice(it),
        quantity: it.quantity || 1,
        variant: variantFromLine(it),
        ...lineCategoryFields(it, lines),
        ...arrangementIdFromLine(it),
      }));
      pushEvent('cart_viewed', {
        source, // 'drawer' | 'page'
        items,
        // Merchandise value: tip and surcharge are left out of items[], so out of the
        // value too; cart-level discounts stay applied (already in total_price).
        value: cart.total_price != null ? Math.max(0, cart.total_price - cartFeeCents(cart)) / 100 : undefined,
      });
    });
}

function initCartViewed() {
  // Cart page load.
  // Not on checkout's "Restore my pickup store" pass (/cart?ea_restore_pickup=1,
  // release/oct-29 EA-PICKUP-POINT): the cart page only re-applies the pickup point
  // and returns to checkout; the shopper never sees a cart there.
  if (window.EAPageData?.pageType === 'cart' && !new URLSearchParams(window.location.search).has('ea_restore_pickup')) {
    emitCartViewed('page');
  }

  // Cart drawer open. dialog:open does NOT bubble, but capturing listeners on
  // ancestors still fire for non-bubbling events — so a single document-level
  // capture-phase listener catches the drawer's open no matter when the
  // <ea-cart-drawer> element is created, removed, or re-created. No per-element
  // binding or MutationObserver state to go stale.
  document.addEventListener(
    'dialog:open',
    (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target?.matches?.('ea-cart-drawer')) emitCartViewed('drawer');
    },
    true,
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   16. SCROLL DEPTH  (GA4 percent_scrolled)
   Fires once per page as scroll crosses 25/50/75/90% (GA4's native scroll only
   fires at 90%). percent_scrolled + pageType (pageType rides shared context).
───────────────────────────────────────────────────────────────────────────── */

function initScrollDepth() {
  const thresholds = [25, 50, 75, 90];
  const fired = new Set();

  const check = () => {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const pct = (window.scrollY / scrollable) * 100;
    thresholds.forEach((t) => {
      if (pct >= t && !fired.has(t)) {
        fired.add(t);
        pushEvent('scroll_depth', { percent_scrolled: t });
      }
    });
    if (fired.size === thresholds.length) {
      window.removeEventListener('scroll', onScroll);
    }
  };

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      check();
      ticking = false;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  check(); // capture initial position (short pages may already pass 25%)
}

/* ─────────────────────────────────────────────────────────────────────────────
   17. DELIVERY METHOD SELECTED  (custom)
   Delivery speed is EA's core value prop but isn't tracked as a first-class
   event. There's no single standardized selector today, so feature code
   (PDP / cart / checkout delivery pickers) should dispatch this event:

     document.dispatchEvent(new CustomEvent('ea:delivery-method-selected', {
       bubbles: true,
       detail: {
         deliveryMethod,   // required — delivery|pickup|shipment
         deliveryDate,     // required — selected date (ISO or display)
         entrySource,      // optional — pdp|cart|checkout
       },
     }));
───────────────────────────────────────────────────────────────────────────── */

function initDeliveryMethod() {
  document.addEventListener('ea:delivery-method-selected', (/** @type {CustomEvent} */ e) => {
    const d = e.detail || {};
    if (!d.deliveryMethod) return;
    pushEvent('delivery_method_selected', {
      deliveryMethod: d.deliveryMethod,
      ...(d.deliveryDate ? { deliveryDate: d.deliveryDate } : {}),
      ...(d.entrySource ? { entrySource: d.entrySource } : {}),
    });
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
   CHECKOUT / PURCHASE — not handled here.
   checkout_step + the split GA4 steps (begin_checkout / add_shipping_info /
   add_payment_info) and purchase run on Shopify's checkout, which this theme
   script cannot reach. They must be emitted from the Shopify Customer Events
   (Web Pixel) sandbox: subscribe to checkout_started /
   checkout_contact_info_submitted / checkout_shipping_info_submitted /
   payment_info_submitted / checkout_completed and push the same event names +
   params (items[], currency, value, transaction.*) so GTM maps them to GA4
   (begin_checkout / add_shipping_info / add_payment_info / purchase), Adobe
   (scCheckout / purchase), and Segment.
───────────────────────────────────────────────────────────────────────────── */

function init() {
  // TEMP(UAT DEBUG): dump the page-level context so we can see whether
  // window.EAPageData rendered and what pageType resolved to. REMOVE after UAT.
  console.log('%c[EA DataLayer] init', 'color:#4fc3f7;font-weight:bold', {
    readyState: document.readyState,
    hasEAPageData: !!window.EAPageData,
    pageType: window.EAPageData?.pageType,
    EAPageData: window.EAPageData,
  });
  assignModulePositions();
  // Auth tracking runs BEFORE the page view so a logout (or a shared-device account
  // switch) resets Segment identity first — initPageView's identify then attributes the
  // page_view to the fresh guest/customer rather than the previous one.
  initAuthTracking();
  initPageView();
  initNavClicks();
  initModuleClicks();
  initModuleViews();
  initFilterApply();
  initSearch();
  initSearchResults();
  initCategoryBrowse();
  initCartAdd();
  initProductRemoved();
  initCouponApplied();
  initProductView();
  initCollectionView();
  initSelectItem();
  initSiteError();
  initFormEvents();
  initZipEntered();
  initLoyaltyRewards();
  initReferralStoreCaptured();
  initCartViewed();
  initScrollDepth();
  initDeliveryMethod();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
